import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch, useApi, getToken } from '../hooks/useApi.js';
import { fmtAbv } from '../utils/format.js';
import { stageStrength, versionStrength } from '../utils/recipeStrength.js';
import { liqueurDensity, apparentAbv } from '../utils/oiml.js';
import { uiAlert, uiConfirm } from '../utils/dialogs.js';
import { ImportCustomerFormModal } from './Clients.jsx';
import PrintButton from '../components/shared/PrintButton.jsx';
import CopyLinkButton from '../components/shared/CopyLinkButton.jsx';
import { useSearchParams } from 'react-router-dom';
import ClientSelect from '../components/shared/ClientSelect.jsx';
import SampleParcelSection from '../components/shared/SampleParcel.jsx';
import { SyrupCalculator } from './Calculators.jsx';
import SimilarStockItems from '../components/shared/SimilarStockItems.jsx';

// ── Primitives ────────────────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'opacity .15s', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1 };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' } };
  return <button {...props} style={{ ...base, ...(v[variant] || v.primary), ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 520, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted)', lineHeight: 1 }}>&#215;</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}><label style={{ fontSize: 13, fontWeight: 500 }}>{label}</label>{children}</div>; }
function Input(props) { return <input {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />; }
function Select({ children, ...props }) { return <select {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' }}>{children}</select>; }
function Textarea(props) { return <textarea {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', color: 'var(--text)', ...props.style }} />; }
function Grid2({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>; }
function BtnRow({ children }) { return <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>{children}</div>; }
function Err({ msg }) { return msg ? <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{msg}</p> : null; }

const STATUS_C = {
  briefed: { bg: '#dbeafe', color: '#1e40af' }, in_development: { bg: '#fef9c3', color: '#854d0e' },
  round_1: { bg: '#ede9fe', color: '#5b21b6' }, round_2: { bg: '#ede9fe', color: '#5b21b6' }, round_3: { bg: '#ede9fe', color: '#5b21b6' }, approved: { bg: '#dcfce7', color: '#166534' },
  converted: { bg: '#dcfce7', color: '#166534' }, cancelled: { bg: '#fee2e2', color: '#991b1b' },
  pending: { bg: '#f3f4f6', color: '#6b7280' }, dispatched: { bg: '#dbeafe', color: '#1e40af' },
  approved_internal: { bg: '#dcfce7', color: '#166534' }, rejected_internal: { bg: '#fee2e2', color: '#991b1b' },
  approved_client: { bg: '#dcfce7', color: '#166534' }, rejected_client: { bg: '#fee2e2', color: '#991b1b' },
  pass: { bg: '#dcfce7', color: '#166534' }, conditional: { bg: '#fef9c3', color: '#854d0e' }, fail: { bg: '#fee2e2', color: '#991b1b' },
  revise: { bg: '#fef9c3', color: '#854d0e' }, rejected: { bg: '#fee2e2', color: '#991b1b' },
};
function StatusBadge({ status }) {
  const c = STATUS_C[status] || { bg: '#f3f4f6', color: '#6b7280' };
  return <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: c.bg, color: c.color }}>{(typeof STATUS_LABEL !== 'undefined' && STATUS_LABEL[status]) || status?.replace(/_/g,' ')}</span>;
}
function fmt(d) { return d ? new Date(d).toLocaleDateString('en-GB') : '—'; }

// ── Create Project modal ──────────────────────────────────────────────────────
function CreateProjectModal({ onClose, onSaved }) {
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData ?? [];
  const [form, setForm] = useState({ client_id: '', project_name: '', spirit_type: '', brief: '', target_abv: '', target_bottle_size_ml: '', target_bulk_litres: '', development_fee: '', notes: '' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch('/recipe-dev', { method: 'POST', body: { ...form, target_abv: form.target_abv ? parseFloat(form.target_abv) : undefined, target_bottle_size_ml: form.target_bottle_size_ml ? parseInt(form.target_bottle_size_ml) : undefined, target_bulk_litres: form.target_bulk_litres ? parseFloat(form.target_bulk_litres) : undefined, development_fee: form.development_fee ? parseFloat(form.development_fee) : undefined } });
      onSaved();
    } catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title="New Recipe Development Project" onClose={onClose}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Field label="Client *"><Select value={form.client_id} onChange={set('client_id')} required><option value="">Select client...</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
        <Grid2>
          <Field label="Project name *"><Input value={form.project_name} onChange={set('project_name')} required placeholder="e.g. Signature Gin Brief" /></Field>
          <Field label="Spirit type *"><Input value={form.spirit_type} onChange={set('spirit_type')} required placeholder="e.g. gin, whisky, rum" /></Field>
        </Grid2>
        <Field label="Client brief *"><Textarea rows={3} value={form.brief} onChange={set('brief')} required placeholder="Describe the client's requirements..." /></Field>
        <Grid2>
          <Field label="Target ABV %"><Input type="number" min="0" max="100" step="0.1" value={form.target_abv} onChange={set('target_abv')} placeholder="e.g. 40" /></Field>
          <Field label="Target bottle size (ml)"><Input type="number" value={form.target_bottle_size_ml} onChange={set('target_bottle_size_ml')} placeholder="e.g. 700" /></Field>
        </Grid2>
        <Grid2>
          <Field label="Target bulk litres"><Input type="number" min="0" step="0.1" value={form.target_bulk_litres} onChange={set('target_bulk_litres')} placeholder="e.g. 500" /></Field>
          <Field label="Development fee (£)"><Input type="number" min="0" step="0.01" value={form.development_fee} onChange={set('development_fee')} placeholder="e.g. 500" /></Field>
        </Grid2>
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Creating...' : 'Create Project'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Create Round modal ────────────────────────────────────────────────────────
function CreateRoundModal({ projectId, roundNumber, versions = [], onClose, onSaved }) {
  const [form, setForm] = useState({ produced_at: 'uk', sample_abv: '', batch_volume_litres: '', notes: '' });
  const free = versions.filter(v => !v.round_id);
  const [versionId, setVersionId] = useState(free.length ? free[free.length - 1].id : '');
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const r = await apiFetch(`/recipe-dev/${projectId}/rounds`, { method: 'POST', body: form });
      const roundId = r?.data?.id || r?.id;
      if (versionId && roundId) await apiFetch(`/recipe-dev/${projectId}/versions/${versionId}/round`, { method: 'PUT', body: { round_id: roundId } });
      onSaved();
    }
    catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title={`Start round ${roundNumber}`} onClose={onClose} width={460}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Grid2>
          <Field label="Sample made in"><Select value={form.produced_at} onChange={set('produced_at')}><option value="uk">UK</option><option value="nz">NZ</option></Select></Field>
          <Field label="Sample ABV %"><Input type="number" min="0" max="100" step="0.1" value={form.sample_abv} onChange={set('sample_abv')} /></Field>
        </Grid2>
        <Field label="Batch volume (litres)"><Input type="number" min="0" step="0.01" value={form.batch_volume_litres} onChange={set('batch_volume_litres')} /></Field>
        {versions.length > 0 && (
          <Field label="Recipe version">
            <Select value={versionId} onChange={e => setVersionId(e.target.value)}>
              <option value="">— none yet —</option>
              {versions.map(v => <option key={v.id} value={v.id}>{vFull(v)}{v.round_number ? ' (already in a round)' : ''}</option>)}
            </Select>
          </Field>
        )}
        <Field label="Recipe notes"><Textarea rows={3} value={form.notes} onChange={set('notes')} placeholder="Botanicals, quantities, distillation approach, what changed from last round..." /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Starting...' : `Start round ${roundNumber}`}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Dispatch modal ────────────────────────────────────────────────────────────
function DispatchModal({ projectId, roundId, onClose, onSaved }) {
  const [form, setForm] = useState({ sample_dispatched_at: new Date().toISOString().slice(0,10), sample_dispatched_from: 'uk' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try { await apiFetch(`/recipe-dev/${projectId}/rounds/${roundId}/dispatch`, { method: 'POST', body: form }); onSaved(); }
    catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title="Sample sent to client" onClose={onClose} width={420}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Grid2>
          <Field label="Date sent (if it has been)"><Input type="date" value={form.sample_dispatched_at} onChange={set('sample_dispatched_at')} /></Field>
          <Field label="Sent from"><Select value={form.sample_dispatched_from} onChange={set('sample_dispatched_from')}><option value="uk">UK</option><option value="nz">NZ</option></Select></Field>
        </Grid2>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Record'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Verdict modal ─────────────────────────────────────────────────────────────
const VERDICTS = {
  approver: [['pass', 'Pass — send to client'], ['conditional', 'Conditional'], ['fail', 'Fail — rework']],
  client: [['approved', 'Approved'], ['revise', 'Revise — another round'], ['rejected', 'Rejected — end the project']],
};
function VerdictModal({ projectId, roundId, type, onClose, onSaved }) {
  const [verdict, setVerdict] = useState(VERDICTS[type][0][0]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const endpoint = `/recipe-dev/${projectId}/rounds/${roundId}/${type === 'approver' ? 'approver' : 'client'}-verdict`;
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    const body = type === 'approver'
      ? { approver_verdict: verdict, approver_tasting_notes: notes }
      : { client_verdict: verdict, client_feedback_raw: notes };
    try { await apiFetch(endpoint, { method: 'POST', body }); onSaved(); }
    catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title={type === 'approver' ? 'Internal verdict' : 'Client verdict'} onClose={onClose} width={440}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Field label="Verdict *"><Select value={verdict} onChange={e => setVerdict(e.target.value)}>{VERDICTS[type].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></Field>
        <Field label={type === 'approver' ? 'Tasting notes' : "Client's feedback"}><Textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)} placeholder={type === 'approver' ? 'Nose, palate, finish...' : 'Paste or summarise what the client said'} /></Field>
        {type === 'client' && verdict !== 'revise' && <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--text-muted)' }}>{verdict === 'approved' ? 'This marks the project Approved.' : 'This cancels the project.'}</p>}
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Record verdict'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Descriptor library helpers ───────────────────────────────────────────────
function useLibrary() {
  const { data, loading, refetch } = useApi('/recipe-dev/descriptors');
  return { groups: Array.isArray(data) ? data : [], loading, refetch };
}
function WordChip({ name, colour, active, onClick, children, title }) {
  const c = colour || '#6b7280';
  return (
    <button type="button" onClick={onClick} title={title}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 99, fontSize: 12.5, fontWeight: 600, cursor: onClick ? 'pointer' : 'default',
        border: `1px solid ${active ? c : 'var(--border)'}`, background: active ? c : 'var(--surface)', color: active ? '#fff' : 'var(--text)' }}>
      {name}{children}
    </button>
  );
}
function Dots({ value, onChange, max = 5, colour = 'var(--pld-green)' }) {
  return (
    <span style={{ display: 'inline-flex', gap: 3 }}>
      {Array.from({ length: max }, (_, i) => i + 1).map(n => (
        <span key={n} onClick={onChange ? (e) => { e.stopPropagation(); onChange(value === n ? 0 : n); } : undefined}
          title={onChange ? `Strength ${n} of ${max}` : undefined}
          style={{ width: 11, height: 11, borderRadius: 99, display: 'inline-block', cursor: onChange ? 'pointer' : 'default',
            background: n <= (value || 0) ? colour : 'var(--surface-2)', border: `1px solid ${n <= (value || 0) ? colour : 'var(--border)'}` }} />
      ))}
    </span>
  );
}

// Pick words from the library (for a brief, or "other words" in a tasting).
// value: { [descriptor_id]: intensity (0 = picked, no strength) }
function WordPicker({ groups, value, onChange, withIntensity = true, intensityLabel = 'target strength', exclude }) {
  const [kind, setKind] = useState('flavour');
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = groups.filter(g => g.kind === kind)
    .map(g => ({ ...g, children: g.children.filter(w => !(exclude && exclude.has(w.id)) && (!needle || w.name.toLowerCase().includes(needle) || g.name.toLowerCase().includes(needle))) }))
    .filter(g => g.children.length);
  const toggle = id => { const next = { ...value }; if (id in next) delete next[id]; else next[id] = 0; onChange(next); };
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {[['flavour', 'Flavour & aroma'], ['mood', 'Brand & mood']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setKind(k)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: kind === k ? 'var(--pld-green)' : 'var(--surface-2)', color: kind === k ? '#fff' : 'var(--text-muted)' }}>{l}</button>
        ))}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search words…" style={{ marginLeft: 'auto', padding: '5px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 12.5, background: 'var(--surface)', color: 'var(--text)', width: 160 }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 340, overflow: 'auto', paddingRight: 4 }}>
        {shown.map(g => (
          <div key={g.id}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: g.colour || 'var(--text-muted)', marginBottom: 5 }}>{g.name}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {g.children.map(w => <WordChip key={w.id} name={w.name} colour={g.colour} active={w.id in value} onClick={() => toggle(w.id)} />)}
            </div>
          </div>
        ))}
        {!shown.length && <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>No words match. Add new ones in the Descriptor library.</p>}
      </div>
      {withIntensity && Object.keys(value).length > 0 && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Chosen — set a {intensityLabel} (optional):</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {Object.keys(value).map(id => {
              const g = groups.find(x => x.children.some(w => w.id === id));
              const w = g?.children.find(x => x.id === id);
              if (!w) return null;
              return (
                <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 99, background: g.colour || '#999' }} />
                  <span style={{ flex: 1 }}>{w.name}</span>
                  <Dots value={value[id]} onChange={n => onChange({ ...value, [id]: n })} colour={g.colour || undefined} />
                  <button type="button" onClick={() => toggle(id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>&#215;</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TargetsModal({ projectId, targets, onClose, onSaved }) {
  const { groups, loading } = useLibrary();
  const [value, setValue] = useState(() => Object.fromEntries(targets.map(t => [t.descriptor_id, t.intensity || 0])));
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  async function save() {
    setSaving(true); setErr('');
    try {
      await apiFetch(`/recipe-dev/${projectId}/targets`, { method: 'PUT', body: { targets: Object.entries(value).map(([descriptor_id, intensity]) => ({ descriptor_id, intensity: intensity || null })) } });
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  }
  return (
    <Modal title="Target words for the brief" onClose={onClose} width={640}>
      <Err msg={err} />
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>Pick the flavours and the feel the client is after. Each sample round is tasted against these words.</p>
      {loading ? <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading library…</p> : <WordPicker groups={groups} value={value} onChange={setValue} />}
      <BtnRow>
        <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
        <Btn type="button" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save target words'}</Btn>
      </BtnRow>
    </Modal>
  );
}

// A taster ticks the words they actually got, and how strongly (1–5).
function TastingModal({ projectId, round, targets, onClose, onSaved }) {
  const { groups, loading } = useLibrary();
  const [tasterType, setTasterType] = useState('internal');
  const [tasterName, setTasterName] = useState('');
  const [targetScores, setTargetScores] = useState({}); // id -> 1..5 (absent = not picked up)
  const [others, setOthers] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const targetIds = new Set(targets.map(t => t.descriptor_id));
  async function save() {
    setSaving(true); setErr('');
    const scores = [
      ...Object.entries(targetScores).filter(([, v]) => v > 0).map(([descriptor_id, intensity]) => ({ descriptor_id, intensity })),
      ...Object.entries(others).filter(([id]) => !targetIds.has(id)).map(([descriptor_id, v]) => ({ descriptor_id, intensity: v || 3 })),
    ];
    try {
      await apiFetch(`/recipe-dev/${projectId}/rounds/${round.id}/tastings`, { method: 'POST', body: { taster_type: tasterType, taster_name: tasterName, notes, scores } });
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  }
  return (
    <Modal title={`Tasting — round ${round.round_number}`} onClose={onClose} width={660}>
      <Err msg={err} />
      <Grid2>
        <Field label="Taster"><Select value={tasterType} onChange={e => setTasterType(e.target.value)}><option value="internal">PLD (internal)</option><option value="client">Client</option></Select></Field>
        <Field label="Name"><Input value={tasterName} onChange={e => setTasterName(e.target.value)} placeholder="Who tasted it" /></Field>
      </Grid2>
      {targets.length > 0 ? (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>The brief's words — how strongly does each come through? <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(leave blank if you don't get it)</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {targets.map(t => (
              <div key={t.descriptor_id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: t.colour || '#999' }} />
                <span style={{ flex: 1 }}>{t.name}{t.intensity ? <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> (target {t.intensity})</span> : null}</span>
                <Dots value={targetScores[t.descriptor_id] || 0} onChange={n => setTargetScores(v => ({ ...v, [t.descriptor_id]: n }))} colour={t.colour || undefined} />
              </div>
            ))}
          </div>
        </div>
      ) : <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No target words on the brief yet — tick whatever you taste below.</p>}
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Anything else you noticed</div>
      {loading ? <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading library…</p> : <WordPicker groups={groups} value={others} onChange={setOthers} intensityLabel="strength" exclude={targetIds} />}
      <Field label="Notes"><Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Nose, palate, finish, anything else" /></Field>
      <BtnRow>
        <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
        <Btn type="button" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save tasting'}</Btn>
      </BtnRow>
    </Modal>
  );
}

// ── Documents ─────────────────────────────────────────────────────────────────
const DOC_TYPES = [['brief', 'Brief'], ['brand', 'Brand / guidelines'], ['spec', 'Spec'], ['lab_result', 'Lab result'], ['tasting_notes', 'Tasting notes'], ['label_artwork', 'Label artwork'], ['photo', 'Photo'], ['correspondence', 'Correspondence'], ['other', 'Other']];
const DOC_LABEL = Object.fromEntries(DOC_TYPES);

function AddDocumentModal({ projectId, rounds, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: '', doc_type: 'brief', round_id: '', url: '', notes: '' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const fd = new FormData();
      if (file) fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const res = await fetch(`/api/recipe-dev/${projectId}/documents`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Upload failed (HTTP ${res.status})`);
      onSaved();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }
  return (
    <Modal title="Add document" onClose={onClose} width={480}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Field label="File"><input type="file" onChange={e => setFile(e.target.files?.[0] ?? null)} /></Field>
        <Field label="…or a link (e.g. Google Drive)"><Input value={form.url} onChange={set('url')} placeholder="https://…" /></Field>
        <Field label="Title"><Input value={form.title} onChange={set('title')} placeholder={file?.name || 'e.g. Client brand deck'} /></Field>
        <Grid2>
          <Field label="Type"><Select value={form.doc_type} onChange={set('doc_type')}>{DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></Field>
          <Field label="Round"><Select value={form.round_id} onChange={set('round_id')}><option value="">Whole project</option>{rounds.map(r => <option key={r.id} value={r.id}>Round {r.round_number}</option>)}</Select></Field>
        </Grid2>
        <Field label="Notes"><Input value={form.notes} onChange={set('notes')} /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || (!file && !form.url.trim())}>{saving ? 'Saving...' : 'Add'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

function DocumentsPanel({ projectId, documents, rounds, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState('all');
  async function open(d) {
    if (!d.has_file) { window.open(d.url, '_blank', 'noopener'); return; }
    try {
      const res = await fetch(`/api/recipe-dev/${projectId}/documents/${d.id}/file`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(`Couldn't open it (HTTP ${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      const w = window.open(url, '_blank');
      if (!w) { const a = document.createElement('a'); a.href = url; a.download = d.file_name || 'document'; a.click(); }
    } catch (e) { uiAlert(e.message); }
  }
  async function remove(d) {
    if (!await uiConfirm(`Remove "${d.title}"?`)) return;
    try { await apiFetch(`/recipe-dev/${projectId}/documents/${d.id}`, { method: 'DELETE' }); onChanged(); }
    catch (e) { uiAlert(e.message); }
  }
  const shown = documents.filter(d => filter === 'all' || d.doc_type === filter);
  const used = [...new Set(documents.map(d => d.doc_type))];
  return (
    <div>
      {adding && <AddDocumentModal projectId={projectId} rounds={rounds} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); onChanged(); }} />}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        {['all', ...used].map(t => (
          <button key={t} onClick={() => setFilter(t)} style={{ padding: '4px 11px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: filter === t ? 'var(--pld-green)' : 'var(--surface-2)', color: filter === t ? '#fff' : 'var(--text-muted)' }}>{t === 'all' ? 'All' : DOC_LABEL[t] || t}</button>
        ))}
        <span style={{ flex: 1 }} />
        <Btn size="sm" onClick={() => setAdding(true)}>+ Add document</Btn>
      </div>
      {shown.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Nothing here yet — keep the brief, brand guidelines, specs, lab results and label drafts with the project.</p> : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          {shown.map((d, i) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderTop: i ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
              <span style={{ fontSize: 18 }}>{d.has_file ? '📄' : '🔗'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <button onClick={() => open(d)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, color: 'var(--text)', textAlign: 'left', fontSize: 13 }}>{d.title}</button>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                  {DOC_LABEL[d.doc_type] || d.doc_type}{d.round_number ? ` · Round ${d.round_number}` : ''} · {d.uploaded_by_name || '—'}, {fmt(d.created_at)}{d.notes ? ` · ${d.notes}` : ''}
                </div>
              </div>
              <button onClick={() => remove(d)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18 }}>&#215;</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Descriptor library page ───────────────────────────────────────────────────
function BotanicalLinker({ word, onChanged }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [err, setErr] = useState('');
  async function search(v) {
    setQ(v); setErr('');
    if (v.trim().length < 2) { setResults([]); return; }
    try { const { stock_items } = await apiFetch(`/stock?search=${encodeURIComponent(v.trim())}`); setResults((stock_items || []).slice(0, 8)); }
    catch (e) { setErr(e.message); }
  }
  async function link(si) {
    try { await apiFetch(`/recipe-dev/descriptors/${word.id}/botanicals`, { method: 'POST', body: { stock_item_id: si.id } }); setQ(''); setResults([]); onChanged(); }
    catch (e) { setErr(e.message); }
  }
  async function unlink(b) {
    try { await apiFetch(`/recipe-dev/descriptors/${word.id}/botanicals/${b.id}`, { method: 'DELETE' }); onChanged(); }
    catch (e) { setErr(e.message); }
  }
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
        {word.botanicals.length ? word.botanicals.map(b => (
          <span key={b.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 99, fontSize: 12, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
            {b.name}<button onClick={() => unlink(b)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>&#215;</button>
          </span>
        )) : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No botanicals linked.</span>}
      </div>
      <Input value={q} onChange={e => search(e.target.value)} placeholder="Link a botanical from stock…" style={{ fontSize: 13, padding: '6px 10px' }} />
      {results.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginTop: 4, maxHeight: 180, overflow: 'auto' }}>
          {results.map(si => <div key={si.id} onClick={() => link(si)} style={{ padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>{si.name} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{si.sku}</span></div>)}
        </div>
      )}
      {err && <p style={{ color: '#dc2626', fontSize: 12, margin: '4px 0 0' }}>{err}</p>}
    </div>
  );
}

function DescriptorLibrary({ onBack }) {
  const { data, loading, refetch } = useApi('/recipe-dev/descriptors?all=true');
  const groups = Array.isArray(data) ? data : [];
  const [kind, setKind] = useState('flavour');
  const [selected, setSelected] = useState(null);
  const [newWord, setNewWord] = useState({});
  const [newGroup, setNewGroup] = useState('');
  const [err, setErr] = useState('');
  const word = selected && groups.flatMap(g => g.children.map(w => ({ ...w, colour: g.colour, group: g.name }))).find(w => w.id === selected);
  async function call(path, method, body) {
    setErr('');
    try { await apiFetch(path, { method, body }); await refetch(); return true; } catch (e) { setErr(e.message); return false; }
  }
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0, marginTop: 4 }}>&#8592;</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Descriptor library</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Shared words for briefs and tastings, grouped like a flavour wheel. Link botanicals to a word so picking it for a brief suggests what to use.</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[['flavour', 'Flavour & aroma'], ['mood', 'Brand & mood']].map(([k, l]) => (
          <button key={k} onClick={() => { setKind(k); setSelected(null); }} style={{ padding: '6px 14px', borderRadius: 99, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', background: kind === k ? 'var(--pld-green)' : 'var(--surface-2)', color: kind === k ? '#fff' : 'var(--text-muted)' }}>{l}</button>
        ))}
      </div>
      <Err msg={err} />
      {loading && !groups.length ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: word ? 'minmax(0,1fr) 300px' : '1fr', gap: 18, alignItems: 'start' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {groups.filter(g => g.kind === kind).map(g => (
              <div key={g.id} style={{ border: '1px solid var(--border)', borderTop: `4px solid ${g.colour || 'var(--border)'}`, borderRadius: 'var(--radius-lg)', padding: '12px 14px', opacity: g.is_active ? 1 : 0.5 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <strong style={{ flex: 1, fontSize: 14 }}>{g.name}</strong>
                  <button onClick={() => call(`/recipe-dev/descriptors/${g.id}`, 'PATCH', { is_active: !g.is_active })} style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer', textDecoration: 'underline' }}>{g.is_active ? 'Hide' : 'Show'}</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                  {g.children.map(w => (
                    <WordChip key={w.id} name={w.name} colour={g.colour} active={selected === w.id} onClick={() => setSelected(selected === w.id ? null : w.id)}
                      title={w.botanicals.length ? `Botanicals: ${w.botanicals.map(b => b.name).join(', ')}` : undefined}>
                      {!w.is_active && <span style={{ fontSize: 10 }}>(hidden)</span>}
                      {w.botanicals.length > 0 && <span style={{ fontSize: 10, opacity: 0.8 }}>🌿{w.botanicals.length}</span>}
                    </WordChip>
                  ))}
                </div>
                <form onSubmit={async e => { e.preventDefault(); if (await call('/recipe-dev/descriptors', 'POST', { name: newWord[g.id], parent_id: g.id })) setNewWord(v => ({ ...v, [g.id]: '' })); }} style={{ display: 'flex', gap: 6 }}>
                  <Input value={newWord[g.id] || ''} onChange={e => setNewWord(v => ({ ...v, [g.id]: e.target.value }))} placeholder="Add a word…" style={{ fontSize: 12.5, padding: '5px 9px' }} />
                  <Btn size="sm" type="submit" disabled={!(newWord[g.id] || '').trim()}>Add</Btn>
                </form>
              </div>
            ))}
            <form onSubmit={async e => { e.preventDefault(); if (await call('/recipe-dev/descriptors', 'POST', { name: newGroup, kind })) setNewGroup(''); }}
              style={{ border: '1px dashed var(--border)', borderRadius: 'var(--radius-lg)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
              <strong style={{ fontSize: 13, color: 'var(--text-muted)' }}>New group</strong>
              <div style={{ display: 'flex', gap: 6 }}>
                <Input value={newGroup} onChange={e => setNewGroup(e.target.value)} placeholder="e.g. Savoury" style={{ fontSize: 12.5, padding: '5px 9px' }} />
                <Btn size="sm" type="submit" disabled={!newGroup.trim()}>Add</Btn>
              </div>
            </form>
          </div>
          {word && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 16, position: 'sticky', top: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: word.colour || 'var(--text-muted)' }}>{word.group}</div>
              <form key={word.id} onSubmit={e => { e.preventDefault(); call(`/recipe-dev/descriptors/${word.id}`, 'PATCH', { name: e.target.elements.wname.value }); }} style={{ display: 'flex', gap: 6, margin: '6px 0 12px' }}>
                <Input name="wname" defaultValue={word.name} style={{ fontSize: 14, fontWeight: 600 }} />
                <Btn size="sm" type="submit">Rename</Btn>
              </form>
              {kind === 'flavour' && (<>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Botanicals that give this</div>
                <BotanicalLinker word={word} onChanged={refetch} />
              </>)}
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between' }}>
                <Btn size="sm" variant="ghost" onClick={() => call(`/recipe-dev/descriptors/${word.id}`, 'PATCH', { is_active: !word.is_active })}>{word.is_active ? 'Hide word' : 'Show word'}</Btn>
                <Btn size="sm" variant="ghost" onClick={() => setSelected(null)}>Close</Btn>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Brief words + round scoring ──────────────────────────────────────────────
function TargetsPanel({ project, onEdit }) {
  const targets = project.creative?.targets ?? [];
  const botanicals = project.creative?.suggested_botanicals ?? [];
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ flex: 1, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>Target words</strong>
        <Btn size="sm" variant="ghost" onClick={onEdit}>{targets.length ? 'Edit words' : '+ Pick target words'}</Btn>
      </div>
      {targets.length === 0 ? <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Pick the flavours and feel the client is after — every sample round gets tasted against them.</p> : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {targets.map(t => (
            <span key={t.descriptor_id} title={t.group_name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 99, fontSize: 12.5, fontWeight: 600, background: (t.colour || '#6b7280') + '1f', color: t.colour || 'var(--text)', border: `1px solid ${(t.colour || '#6b7280')}55` }}>
              {t.name}{t.intensity ? <Dots value={t.intensity} colour={t.colour || undefined} /> : null}
            </span>
          ))}
        </div>
      )}
      {botanicals.length > 0 && (
        <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
          <strong>Botanicals to consider:</strong> {botanicals.map(b => `${b.name} (${(b.for_words || []).join(', ')})`).join(' · ')}
        </p>
      )}
    </div>
  );
}

function RoundScore({ projectId, score, onChanged }) {
  if (!score) return null;
  const { match, per_target: per, also_noticed: also, tastings } = score;
  async function removeTasting(t) {
    if (!await uiConfirm('Remove this tasting?')) return;
    try { await apiFetch(`/recipe-dev/${projectId}/tastings/${t.id}`, { method: 'DELETE' }); onChanged(); } catch (e) { uiAlert(e.message); }
  }
  if (!tastings.length) return <div style={{ padding: '0 16px 14px', fontSize: 12.5, color: 'var(--text-muted)' }}>No tastings recorded yet — add one to see how close this round is to the brief.</div>;
  return (
    <div style={{ padding: '0 16px 14px' }}>
      {match != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Brief match</span>
          <div style={{ flex: 1, maxWidth: 220, height: 8, borderRadius: 99, background: 'var(--surface-2)', overflow: 'hidden' }}>
            <div style={{ width: `${match}%`, height: '100%', background: match >= 75 ? '#16a34a' : match >= 45 ? '#d97706' : '#dc2626' }} />
          </div>
          <strong style={{ fontSize: 14 }}>{match}%</strong>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>from {tastings.length} tasting{tastings.length === 1 ? '' : 's'}</span>
        </div>
      )}
      {per.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '4px 16px', fontSize: 12.5, marginBottom: 8 }}>
          {per.map(p => (
            <div key={p.descriptor_id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: p.picked_up > 0 ? '#16a34a' : '#dc2626' }}>{p.picked_up > 0 ? '✓' : '✗'}</span>
              <span style={{ flex: 1 }}>{p.name}</span>
              <span style={{ color: 'var(--text-muted)' }} title="Share of tasters who picked it up · average strength (target)">{Math.round((p.picked_up || 0) * 100)}% got it{p.avg_intensity != null ? ` · strength ${p.avg_intensity}${p.target_intensity ? ` (aim ${p.target_intensity})` : ''}` : ''}</span>
            </div>
          ))}
        </div>
      )}
      {also.length > 0 && (
        <p style={{ margin: '0 0 8px', fontSize: 12.5 }}>
          <span style={{ color: 'var(--text-muted)' }}>Also noticed: </span>
          {also.map((a, i) => <span key={a.descriptor_id} style={{ color: a.off_note ? '#dc2626' : undefined, fontWeight: a.off_note ? 600 : 400 }}>{i ? ', ' : ''}{a.name}{a.count > 1 ? ` ×${a.count}` : ''}{a.off_note ? ' (off-note)' : ''}</span>)}
        </p>
      )}
      <details>
        <summary style={{ fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>Tastings ({tastings.length})</summary>
        {tastings.map(t => (
          <div key={t.id} style={{ fontSize: 12.5, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
            <strong>{t.taster_name || (t.taster_type === 'client' ? 'Client' : 'PLD')}</strong> <span style={{ color: 'var(--text-muted)' }}>({t.taster_type === 'client' ? 'client' : 'internal'}, {fmt(t.created_at)})</span>
            <button onClick={() => removeTasting(t)} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>&#215;</button>
            <div>{t.scores.map(s => `${s.name} ${s.intensity}`).join(' · ') || '—'}</div>
            {t.notes && <div style={{ color: 'var(--text-muted)' }}>{t.notes}</div>}
          </div>
        ))}
      </details>
    </div>
  );
}

// ── Recipe versions ───────────────────────────────────────────────────────────
const UNITS = ['g', 'kg', 'ml', 'L', 'units', 'drops'];
// R1.2 (round 1, iteration 2), or "Draft 3" for a version not in a round.
const vLabel = v => v?.label || v?.name || 'Version';
const vFull = v => (v?.name ? `${vLabel(v)} — ${v.name}` : vLabel(v));
const TO_BASE = { g: ['kg', 0.001], kg: ['kg', 1], mg: ['kg', 0.000001], ml: ['L', 0.001], L: ['L', 1], litres: ['L', 1] };
function convert(amount, from, to) {
  const a = TO_BASE[from]; const b = TO_BASE[to === 'litres' ? 'L' : to];
  if (!a || !b || a[0] !== b[0]) return null;
  return (amount * a[1]) / b[1];
}
function niceQty(amount, unit) {
  if (amount == null || !Number.isFinite(amount)) return '—';
  let a = amount; let u = unit || '';
  if (u === 'g' && a >= 1000) { a /= 1000; u = 'kg'; }
  if (u === 'ml' && a >= 1000) { a /= 1000; u = 'L'; }
  if (u === 'kg' && a > 0 && a < 0.1) { a *= 1000; u = 'g'; }
  if (u === 'L' && a > 0 && a < 0.1) { a *= 1000; u = 'ml'; }
  const dp = a >= 100 ? 1 : a >= 1 ? 2 : a >= 0.01 ? 3 : 5;
  return `${Number(a.toFixed(dp)).toLocaleString('en-GB')} ${u}`.trim();
}
// Cost of `amount unit` of an ingredient: from the form's cost, else the stock item's standard cost.
function lineCost(g, amount) {
  if (amount == null) return null;
  if (g.cost_per_unit != null && g.cost_unit) {
    const q = convert(amount, g.unit, g.cost_unit);
    if (q != null) return q * Number(g.cost_per_unit);
  }
  if (g.standard_cost != null && g.item_unit) {
    const q = convert(amount, g.unit, g.item_unit);
    if (q != null) return q * Number(g.standard_cost);
  }
  return null;
}

// A new ingredient straight into stock, from a recipe (Joe, 3 Nov 2026),
// with a check for anything already in stock that looks like the same thing.
function NewIngredientModal({ initialName = '', onClose, onCreated }) {
  const [form, setForm] = useState({ name: initialName, category: 'raw_material', unit: 'kg', is_alcoholic: false, abv: '', standard_cost: '' });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(''); const [dup, setDup] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  async function save(e, allowDuplicate = false) {
    setBusy(true); setErr(''); setDup(null);
    try {
      const r = await apiFetch('/recipe-dev/ingredients', { method: 'POST', body: {
        ...form, abv: form.is_alcoholic && form.abv !== '' ? Number(form.abv) : null,
        standard_cost: form.standard_cost === '' ? undefined : Number(form.standard_cost), allow_duplicate: allowDuplicate,
      } });
      onCreated(r.stock_item);
    } catch (e2) {
      setErr(e2.message);
      if (/already in stock/.test(e2.message)) setDup(true);
    } finally { setBusy(false); }
  }
  return (
    <Modal title="New ingredient in stock" onClose={onClose} width={480}>
      {/* Not a <form>: this opens inside the recipe's own form, and Enter or a
          submit button here would save the recipe instead. */}
      <div onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); if (form.name.trim() && !busy) save(); } }}>
        <Field label="Name"><Input autoFocus value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Pink Peppercorn - Organic" /></Field>
        <div style={{ marginTop: -6, marginBottom: 12 }}>
          <SimilarStockItems name={form.name} onUse={it => onCreated(it)} useLabel="Use this one" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Kind"><Select value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="raw_material">Raw material</option>
            <option value="semi_product">Semi product (e.g. a distillate)</option>
            <option value="consumable">Consumable</option>
            <option value="packaging">Packaging</option>
          </Select></Field>
          <Field label="Counted in"><Select value={form.unit} onChange={e => set('unit', e.target.value)}>
            {['kg', 'g', 'litres', 'ml', 'units'].map(u => <option key={u} value={u}>{u}</option>)}
          </Select></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, alignItems: 'end' }}>
          <Field label={`Cost per ${form.unit} (£, optional)`}><Input type="number" min="0" step="0.0001" value={form.standard_cost} onChange={e => set('standard_cost', e.target.value)} /></Field>
          <Field label="Alcoholic?">
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={form.is_alcoholic} onChange={e => set('is_alcoholic', e.target.checked)} /> Yes
              {form.is_alcoholic && <Input type="number" min="0" max="100" step="0.1" value={form.abv} onChange={e => set('abv', e.target.value)} placeholder="ABV %" style={{ width: 90, padding: '6px 8px' }} />}
            </label>
          </Field>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>It goes into stock with a new SKU, ready to order; anything else (supplier, reorder level) can be filled in on the Stock page.</p>
        {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 8px' }}>{err}</p>}
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          {dup && <Btn variant="ghost" type="button" disabled={busy} onClick={() => save(null, true)} title="Only if it really is a different thing">It's different — add anyway</Btn>}
          <Btn type="button" onClick={() => save()} disabled={busy || !form.name.trim()}>{busy ? 'Adding…' : 'Add to stock'}</Btn>
        </BtnRow>
      </div>
    </Modal>
  );
}

function StockPicker({ value, label, onPick, placeholder = 'Search stock…' }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(null); // name for a new ingredient
  async function search(v) {
    setQ(v);
    if (v.trim().length < 2) { setResults([]); return; }
    try { const { stock_items } = await apiFetch(`/stock?search=${encodeURIComponent(v.trim())}`); setResults((stock_items || []).slice(0, 10)); } catch { setResults([]); }
  }
  return (
    <div style={{ position: 'relative' }}>
      {/* Portalled to the page: the picker sits inside the recipe's form. */}
      {adding !== null && createPortal(<NewIngredientModal initialName={adding} onClose={() => setAdding(null)} onCreated={si => { setAdding(null); onPick(si); }} />, document.body)}
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{ width: '100%', textAlign: 'left', padding: '6px 9px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--surface-2)', fontSize: 12.5, cursor: 'pointer', color: value ? 'var(--text)' : 'var(--text-muted)' }}>
          {value ? label : placeholder}
        </button>
      ) : (
        <input autoFocus value={q} onChange={e => search(e.target.value)} onBlur={() => setTimeout(() => setOpen(false), 200)} placeholder={placeholder}
          style={{ width: '100%', boxSizing: 'border-box', padding: '6px 9px', borderRadius: 'var(--radius)', border: '1px solid var(--pld-green)', fontSize: 12.5, background: 'var(--surface)', color: 'var(--text)' }} />
      )}
      {open && q.trim().length >= 2 && (
        <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)', maxHeight: 240, overflow: 'auto' }}>
          {results.map(si => (
            <div key={si.id} onMouseDown={() => { onPick(si); setOpen(false); setQ(''); }} style={{ padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
              {si.name} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{si.sku} · {si.unit}</span>
            </div>
          ))}
          <div onMouseDown={() => { setAdding(q.trim()); setOpen(false); setQ(''); }} style={{ padding: '7px 10px', fontSize: 12.5, cursor: 'pointer', color: 'var(--pld-green)', fontWeight: 600 }}>
            + Add "{q.trim()}" to stock as a new ingredient…
          </div>
        </div>
      )}
    </div>
  );
}

// How much of each stage goes into 1 L of the finished product (the last
// stage makes 1 L; an earlier stage makes what the later ones use of it).
// factor[n] multiplies stage n's amounts, which are written per its own basis.
const litresOf = g => (g.unit === 'ml' ? Number(g.amount) / 1000 : g.unit === 'g' ? Number(g.amount) / 1000 : Number(g.amount));
function stageFactors(stages, lines) {
  const need = {}; const factor = {};
  const last = stages[stages.length - 1];
  for (let i = stages.length - 1; i >= 0; i -= 1) {
    const st = stages[i];
    need[st.stage_number] = st === last ? 1 : lines.filter(g => g.from_stage === st.stage_number && g.amount != null)
      .reduce((t, g) => t + litresOf(g) * (factor[g.stage_number] || 0), 0);
    factor[st.stage_number] = need[st.stage_number] / (Number(st.basis_litres) || 1);
  }
  return { need, factor };
}
const isStaged = v => (v?.stages?.length || 0) >= 2;

function VersionEditor({ projectId, rounds, version, copyFrom, onClose, onSaved }) {
  const src = version || copyFrom;
  const tweak = !version && !!copyFrom; // a new iteration of copyFrom
  const [form, setForm] = useState({
    name: version ? (version.name || '') : '',
    basis_litres: src?.basis_litres != null ? String(Number(src.basis_litres)) : '1',
    method: src?.method || '', notes: version ? (version.notes || '') : '',
    round_id: version ? (version.round_id || '') : (copyFrom?.round_id || ''),
    reason: '',
  });
  const [stages, setStages] = useState(() => (isStaged(src) ? src.stages.map(st => ({
    name: st.name || '', basis_litres: String(Number(st.basis_litres || 1)), target_abv: st.target_abv != null ? String(Number(st.target_abv)) : '', method: st.method || '',
  })) : []));
  const [rows, setRows] = useState(() => (src?.ingredients || []).map(g => ({
    stock_item_id: g.stock_item_id, label: g.item_name || g.name_as_written, name_as_written: g.name_as_written || g.item_name,
    amount: g.amount != null ? String(Number(g.amount)) : '', unit: g.unit || (g.from_stage ? 'L' : 'g'), section: g.section, cost_per_unit: g.cost_per_unit, cost_unit: g.cost_unit,
    stage_number: g.stage_number || null, from_stage: g.from_stage || null,
  })));
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setRow = (i, patch) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = (section, stageNo = null, fromStage = null) => setRows(rs => [...rs, {
    stock_item_id: null, label: '', name_as_written: fromStage ? (stages[fromStage - 1]?.name || `Stage ${fromStage}`) : '', amount: '',
    unit: fromStage ? 'L' : 'g', section, stage_number: stageNo, from_stage: fromStage,
  }]);
  const setStage = (i, k) => e => setStages(ss => ss.map((st, j) => (j === i ? { ...st, [k]: e.target.value } : st)));
  // Into stages: what's there becomes stage 1, with a stage 2 to add to.
  const makeStaged = () => {
    setStages([{ name: 'Stage 1', basis_litres: form.basis_litres || '1', target_abv: '', method: form.method || '' }, { name: 'Stage 2', basis_litres: form.basis_litres || '1', target_abv: '', method: '' }]);
    setRows(rs => rs.map(r => ({ ...r, stage_number: 1, from_stage: null, section: 'charge' })));
    setForm(f => ({ ...f, method: '' }));
  };
  const addStage = () => setStages(ss => [...ss, { name: `Stage ${ss.length + 1}`, basis_litres: ss[ss.length - 1]?.basis_litres || '1', target_abv: '', method: '' }]);
  const removeStage = async i => {
    const n = i + 1;
    if (rows.some(r => r.stage_number === n && !r.from_stage) && !await uiConfirm(`Remove stage ${n} and its ingredients?`)) return;
    // Drop its lines and any use of its output; later stages move up one.
    setRows(rs => rs.filter(r => r.stage_number !== n && r.from_stage !== n)
      .map(r => ({ ...r, stage_number: r.stage_number > n ? r.stage_number - 1 : r.stage_number, from_stage: r.from_stage > n ? r.from_stage - 1 : r.from_stage })));
    const left = stages.filter((_, j) => j !== i);
    if (left.length < 2) {
      // Back to a single-stage recipe.
      setForm(f => ({ ...f, basis_litres: left[0]?.basis_litres || f.basis_litres, method: [f.method, left[0]?.method].filter(Boolean).join('\n') }));
      setRows(rs => rs.map(r => ({ ...r, stage_number: null, from_stage: null })));
      setStages([]);
    } else setStages(left);
  };
  async function save() {
    setSaving(true); setErr('');
    try {
      if (tweak && !form.reason.trim()) throw new Error('Say what you changed and why — it goes on the record');
      if (stages.length && stages.some(st => !st.name.trim())) throw new Error('Give each stage a name');
      const body = {
        ...form,
        stages: stages.map(st => ({ ...st, basis_litres: Number(st.basis_litres) || 1, target_abv: st.target_abv === '' ? null : Number(st.target_abv) })),
        ingredients: rows.map(r => ({ ...r, amount: r.amount === '' ? null : Number(r.amount) })),
        ...(tweak ? { parent_version_id: copyFrom.id } : {}),
      };
      if (version) await apiFetch(`/recipe-dev/${projectId}/versions/${version.id}`, { method: 'PUT', body });
      else await apiFetch(`/recipe-dev/${projectId}/versions`, { method: 'POST', body });
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  }
  const lineRow = (r, i) => (
    <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 90px 80px 28px', gap: 6, marginBottom: 5, alignItems: 'center' }}>
      <div>
        {r.from_stage ? (
          <div style={{ padding: '6px 9px', borderRadius: 'var(--radius)', border: '1px dashed var(--pld-green)', background: '#f3f7ee', fontSize: 12.5 }}>
            ↳ {stages[r.from_stage - 1]?.name || `Stage ${r.from_stage}`} <span style={{ color: 'var(--text-muted)' }}>(made in stage {r.from_stage})</span>
          </div>
        ) : (
          <StockPicker value={r.stock_item_id} label={r.label} placeholder={r.name_as_written ? `${r.name_as_written} (not in stock) — pick…` : 'Pick an ingredient…'}
            onPick={si => setRow(i, { stock_item_id: si.id, label: si.name, name_as_written: r.name_as_written || si.name })} />
        )}
      </div>
      <Input type="number" step="any" min="0" value={r.amount} onChange={e => setRow(i, { amount: e.target.value })} style={{ padding: '6px 8px', fontSize: 12.5 }} />
      <select value={r.unit || ''} onChange={e => setRow(i, { unit: e.target.value })} style={{ padding: '6px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 12.5, background: 'var(--surface-2)', color: 'var(--text)' }}>
        {!r.unit && <option value="">unit?</option>}
        {(r.from_stage ? ['ml', 'L'] : UNITS).map(u => <option key={u} value={u}>{u}</option>)}
      </select>
      <button type="button" onClick={() => setRows(rs => rs.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18 }}>&#215;</button>
    </div>
  );
  const section = (key, title) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>{title}</div>
      {rows.map((r, i) => (r.section !== key ? null : lineRow(r, i)))}
      <Btn size="sm" variant="ghost" type="button" onClick={() => addRow(key)}>+ Add</Btn>
    </div>
  );
  const smallInput = { padding: '6px 8px', fontSize: 12.5 };
  return (
    <Modal title={version ? `Edit ${vFull(version)}` : tweak ? `Tweak ${vLabel(copyFrom)} — new iteration` : 'New draft'} onClose={onClose} width={720}>
      <Err msg={err} />
      {tweak && (
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 14 }}>
          <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)' }}>
            {vLabel(copyFrom)} stays exactly as it is. Your changes become the next iteration{form.round_id ? ` in round ${rounds.find(r => r.id === form.round_id)?.round_number ?? ''}` : ''}, and what changed is recorded with your reason.
          </p>
          <Field label="What did you change, and why? *"><Textarea rows={2} value={form.reason} onChange={set('reason')} placeholder="e.g. Too piney on the nose — juniper down 10 g → 8 g, added pink peppercorn" autoFocus /></Field>
        </div>
      )}
      <Grid2>
        <Field label="Name (optional)"><Input value={form.name} onChange={set('name')} placeholder="e.g. less juniper" /></Field>
        <Grid2>
          {stages.length
            ? <Field label="Made in"><div style={{ padding: '9px 0', fontSize: 13 }}>{stages.length} stages</div></Field>
            : <Field label="Amounts are per (L)"><Input type="number" step="any" min="0.001" value={form.basis_litres} onChange={set('basis_litres')} /></Field>}
          <Field label="Round"><Select value={form.round_id} onChange={set('round_id')}><option value="">Draft (no round)</option>{rounds.map(r => <option key={r.id} value={r.id}>Round {r.round_number}</option>)}</Select></Field>
        </Grid2>
      </Grid2>
      {!stages.length ? (
        <>
          {section('charge', 'Ingredients (still charge or blend)')}
          {section('addition', 'Added per litre after distilling')}
          <div style={{ margin: '-4px 0 14px' }}>
            <Btn size="sm" variant="ghost" type="button" onClick={makeStaged} title="e.g. a macerate made first, then used in the blend">Made in stages? Split into stages</Btn>
          </div>
          <Field label="Method"><Textarea rows={3} value={form.method} onChange={set('method')} placeholder="e.g. 50/50 GNS water, macerate overnight, fresh ingredients in the vapour basket" /></Field>
        </>
      ) : (
        <>
          {stages.map((st, si) => {
            const n = si + 1;
            return (
              <div key={si} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '10px 12px', marginBottom: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '70px minmax(0,1fr) 110px 90px 28px', gap: 6, alignItems: 'end', marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--pld-green)', paddingBottom: 8 }}>Stage {n}</div>
                  <Field label="Name"><Input value={st.name} onChange={setStage(si, 'name')} placeholder="e.g. Lime macerate" style={smallInput} /></Field>
                  <Field label="Amounts per (L)"><Input type="number" step="any" min="0.001" value={st.basis_litres} onChange={setStage(si, 'basis_litres')} style={smallInput} /></Field>
                  <Field label="ABV %"><Input type="number" step="any" min="0" max="100" value={st.target_abv} onChange={setStage(si, 'target_abv')} style={smallInput} /></Field>
                  <button type="button" title={`Remove stage ${n}`} onClick={() => removeStage(si)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18, paddingBottom: 10 }}>&#215;</button>
                </div>
                {rows.map((r, i) => (r.stage_number !== n ? null : lineRow(r, i)))}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 8px' }}>
                  <Btn size="sm" variant="ghost" type="button" onClick={() => addRow('charge', n)}>+ Add ingredient</Btn>
                  {stages.slice(0, si).map((prevSt, pi) => (
                    <Btn key={pi} size="sm" variant="ghost" type="button" onClick={() => addRow('charge', n, pi + 1)}>+ Use stage {pi + 1} ({prevSt.name || `Stage ${pi + 1}`})</Btn>
                  ))}
                </div>
                <Textarea rows={2} value={st.method} onChange={setStage(si, 'method')} placeholder={`How stage ${n} is made`} style={{ fontSize: 12.5 }} />
              </div>
            );
          })}
          <div style={{ marginBottom: 14 }}><Btn size="sm" variant="ghost" type="button" onClick={addStage}>+ Add stage</Btn></div>
          <p style={{ margin: '-6px 0 12px', fontSize: 12, color: 'var(--text-muted)' }}>The last stage is the finished product. Blake works out how much of each earlier stage a batch needs from what the later stages use.</p>
        </>
      )}
      <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
      {version && <Field label="Reason for this change (goes on the record)"><Input value={form.reason} onChange={set('reason')} placeholder="optional" /></Field>}
      <BtnRow>
        <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
        <Btn type="button" onClick={save} disabled={saving}>{saving ? 'Saving...' : tweak ? 'Save new iteration' : 'Save'}</Btn>
      </BtnRow>
    </Modal>
  );
}

// The record of every iteration and change on a project.
const CHANGE_LINE = {
  added: c => `+ ${c.name} ${c.to}${c.section === 'addition' ? ' (after distilling)' : ''}`,
  removed: c => `− ${c.name} (was ${c.from})`,
  changed: c => `${c.name}: ${c.from} → ${c.to}`,
  section: c => `${c.name} moved ${c.from} → ${c.to}`,
  basis: c => `Batch basis ${c.from} → ${c.to}`,
  method: () => 'Method changed',
  round: c => `Moved ${c.from} → ${c.to}`,
  stage: c => (!c.from ? `+ ${c.name}: ${c.to}` : !c.to ? `− ${c.name} (was ${c.from})` : `${c.name}: ${c.from} → ${c.to}`),
  recipe: c => `Made into production recipe "${c.to}"`,
};
const KIND = { created: 'started', tweaked: 'tweaked from', edited: 'edited', deleted: 'deleted', moved: 'moved' };
function ChangeRecord({ changes }) {
  const [open, setOpen] = useState(false);
  if (!changes.length) return null;
  const shown = open ? changes : changes.slice(0, 3);
  return (
    <div style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '10px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <strong style={{ fontSize: 12, textTransform: 'uppercase', color: 'var(--text-muted)', flex: 1 }}>Record of changes ({changes.length})</strong>
        {changes.length > 3 && <Btn size="sm" variant="ghost" onClick={() => setOpen(o => !o)}>{open ? 'Show fewer' : 'Show all'}</Btn>}
      </div>
      {shown.map(c => (
        <div key={c.id} style={{ borderTop: '1px solid var(--border)', padding: '7px 0', fontSize: 12.5 }}>
          <div>
            <strong>{c.version_label || 'A version'}</strong> {KIND[c.kind] || c.kind}{c.kind === 'tweaked' && c.from_label ? ` ${c.from_label}` : ''}
            <span style={{ color: 'var(--text-muted)' }}> · {c.created_by_name || '—'}, {new Date(c.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          {c.reason && <div style={{ fontStyle: 'italic', margin: '2px 0' }}>"{c.reason}"</div>}
          {c.changes?.length > 0 && (
            <ul style={{ margin: '2px 0 0', paddingLeft: 18, color: 'var(--text-muted)' }}>
              {c.changes.map((x, i) => <li key={i}>{(CHANGE_LINE[x.type] || (() => x.name))(x)}</li>)}
            </ul>
          )}
          {c.kind === 'tweaked' && !c.changes?.length && <div style={{ color: 'var(--text-muted)' }}>No recipe changes (same ingredients)</div>}
        </div>
      ))}
    </div>
  );
}

// Say which sample round each version went out in — mainly for going back
// over imported versions. A round that isn't in Blake yet is recorded as a
// past round (date sent, where it was made).
function MapRoundsModal({ project, onClose, onSaved }) {
  const versions = project.versions ?? [];
  const rounds = project.rounds ?? [];
  const max = project.max_rounds || 3;
  const byNum = Object.fromEntries(rounds.map(r => [r.round_number, r]));
  const [assign, setAssign] = useState(() => Object.fromEntries(versions.map(v => [v.id, v.round_number ? String(v.round_number) : ''])));
  const [past, setPast] = useState({}); // round number -> { sample_dispatched_at, produced_at, notes }
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const newNums = [...new Set(Object.values(assign).filter(n => n && !byNum[n]))].map(Number).sort((a, b) => a - b);
  async function save() {
    setBusy(true); setErr('');
    try {
      const changed = Object.fromEntries(versions.filter(v => String(v.round_number || '') !== assign[v.id]).map(v => [v.id, assign[v.id] ? Number(assign[v.id]) : null]));
      await apiFetch(`/recipe-dev/${project.id}/version-rounds`, { method: 'PUT', body: { assign: changed, rounds: past } });
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  }
  const setP = (n, k) => e => setPast(p => ({ ...p, [n]: { produced_at: 'uk', ...p[n], [k]: e.target.value } }));
  return (
    <Modal title="Versions and sample rounds" onClose={onClose} width={620}>
      <Err msg={err} />
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>Pick the round each version belongs to. Linking doesn't mark anything as sent — rounds not in Blake yet are added, and you only give a date if the sample has already gone out.</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 14 }}>
        <tbody>
          {versions.map(v => (
            <tr key={v.id} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '6px 4px' }}><strong>{vFull(v)}</strong>{v.source === 'import' && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>imported</span>}</td>
              <td style={{ padding: '6px 4px', width: 230 }}>
                <Select value={assign[v.id]} onChange={e => setAssign(a => ({ ...a, [v.id]: e.target.value }))}>
                  <option value="">No round</option>
                  {Array.from({ length: max }, (_, i) => i + 1).map(n => (
                    <option key={n} value={String(n)}>Round {n}{byNum[n] ? (byNum[n].sample_dispatched_at ? ` (sent ${fmt(byNum[n].sample_dispatched_at)})` : '') : ' — add this round'}</option>
                  ))}
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {newNums.map(n => (
        <div key={n} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 10, background: 'var(--surface-2)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>New round {n}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '150px 110px 1fr', gap: 10 }}>
            <Field label="Date sent"><Input type="date" value={past[n]?.sample_dispatched_at || ''} onChange={setP(n, 'sample_dispatched_at')} /></Field>
            <Field label="Made in"><Select value={past[n]?.produced_at || 'uk'} onChange={setP(n, 'produced_at')}><option value="uk">UK</option><option value="nz">NZ</option></Select></Field>
            <Field label="Notes"><Input value={past[n]?.notes || ''} onChange={setP(n, 'notes')} placeholder="optional" /></Field>
          </div>
        </div>
      ))}
      {newNums.length > 0 && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>The verdicts and client feedback can be added on each round afterwards.</p>}
      <BtnRow>
        <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
        <Btn type="button" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Btn>
      </BtnRow>
    </Modal>
  );
}

// Bottle labels for samples: the 100×150 label printer, two labels a page
// with a cut line halfway down. Each half can be a different version.
function SampleLabelModal({ project, version, onClose }) {
  const versions = project.versions ?? [];
  const rounds = project.rounds ?? [];
  const today = new Date().toLocaleDateString('en-GB');
  const make = (v) => {
    const r = rounds.find(x => x.id === v?.round_id);
    const abv = r?.sample_abv ?? project.target_abv;
    return {
      version_id: v?.id || '',
      product: project.project_name || project.spirit_type || '',
      client: project.client_name || '',
      round: v?.round_number ? String(v.round_number) : '',
      abv: abv != null ? fmtAbv(abv) : '',
      size: '',
      date: today,
      note: '',
    };
  };
  const [top, setTop] = useState(() => make(version));
  const [same, setSame] = useState(true);
  const [bottom, setBottom] = useState(() => make(version));
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const parcelRef = useRef(null);
  const [sending, setSending] = useState(false);
  // The round these samples are from (to mark it sent and show what went).
  const roundOf = (l) => rounds.find(x => x.id === versions.find(v => v.id === l.version_id)?.round_id)
    || rounds.find(x => l.round && String(x.round_number) === String(l.round));
  const sampleRound = roundOf(top);
  const body = () => {
    const strip = ({ version_id, ...l }) => l;
    return { top: strip(top), bottom: same ? strip({ ...top }) : strip(bottom) };
  };
  async function preview() {
    setBusy(true); setErr('');
    const win = window.open('', '_blank');
    try {
      const res = await fetch(`/api/recipe-dev/${project.id}/sample-label.pdf`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify(body()) });
      if (!res.ok) throw new Error(`Couldn't make the label (HTTP ${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (win) win.close(); setErr(e.message); } finally { setBusy(false); }
  }
  const half = (l, setL, title) => {
    const set = k => e => setL(x => ({ ...x, [k]: e.target.value }));
    return (
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>{title}</div>
        {versions.length > 0 && (
          <Field label="Fill in from version (not printed)">
            <Select value={l.version_id} onChange={e => { const v = versions.find(x => x.id === e.target.value); setL(x => ({ ...make(v), size: x.size, date: x.date, note: x.note, product: x.product })); }}>
              <option value="">—</option>
              {versions.map(v => <option key={v.id} value={v.id}>{vFull(v)}</option>)}
            </Select>
          </Field>
        )}
        <Grid2>
          <Field label="Product name"><Input value={l.product} onChange={set('product')} /></Field>
          <Field label="Client"><ClientSelect value={l.client} onChange={(v) => setL(x => ({ ...x, client: v }))} /></Field>
        </Grid2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          <Field label="Round"><Input value={l.round} onChange={set('round')} /></Field>
          <Field label="ABV %"><Input value={l.abv} onChange={set('abv')} /></Field>
          <Field label="Size"><Input value={l.size} onChange={set('size')} placeholder="e.g. 5cl" /></Field>
          <Field label="Date"><Input value={l.date} onChange={set('date')} /></Field>
        </div>
        <div>
          <Field label="Footer"><Input value={l.note} onChange={set('note')} placeholder="Not for sale · for evaluation only" /></Field>
        </div>
      </div>
    );
  };
  return (
    <Modal title="Sample bottle labels" onClose={onClose} width={620}>
      <Err msg={err} />
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>Prints on the 100×150 label printer as two labels with a cut line halfway down — cut it in half for two bottles. The label goes to the customer, so it shows the round, not our version names.</p>
      {half(top, setTop, same ? 'Both halves' : 'Top half')}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '0 0 10px' }}>
        <input type="checkbox" checked={same} onChange={e => { setSame(e.target.checked); if (!e.target.checked) setBottom({ ...top }); }} />
        Same label on both halves
      </label>
      {!same && half(bottom, setBottom, 'Bottom half')}
      <SampleParcelSection ref={parcelRef} clientName={top.client} kind="development" onSendChange={setSending} projectId={project.id} roundId={sampleRound?.id}
        reference={`${project.project_ref || ''}${sampleRound ? ` R${sampleRound.round_number}` : ''}`.trim()}
        description={`Samples — ${top.product || 'development'}`.slice(0, 35)} />
      <BtnRow>
        <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
        <Btn variant="ghost" type="button" onClick={preview} disabled={busy}>{busy ? 'Making…' : 'Preview'}</Btn>
        <PrintButton docType="sample_label" method="POST" path={`/recipe-dev/${project.id}/sample-label.pdf`} getBody={body} title={`Sample labels — ${top.product}${top.round ? ` round ${top.round}` : ''}`} size="md"
          label={sending ? 'Print & send' : 'Print'} onDone={() => parcelRef.current?.queue()} />
      </BtnRow>
    </Modal>
  );
}

// Predicted strength and sugar under a stage / ingredient table (21 Oct 2026):
// the design-side use of the OIML calculators.
function StrengthLine({ s, target, label, additions }) {
  if (!s) return null;
  const f = (v, d = 1) => Number(v).toLocaleString('en-GB', { minimumFractionDigits: d, maximumFractionDigits: d });
  const unknown = [...s.unknownSpirits.map(n => `${n} (no ABV — set it on the stock item, or put the % in its name)`), ...s.unknownSugars.map(n => (/\(/.test(n) ? n : `${n} (sugar content unknown — set it on the stock item, under Composition)`))];
  if (additions) {
    if (!(s.sugar > 0) && !unknown.length) return null;
    return (
      <div style={{ margin: '4px 6px 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
        <strong style={{ color: 'var(--text)' }}>Adds {f(s.sugar, 0)} g/L sugar</strong>
        {target != null && s.sugar > 0 && (() => {
          const d = liqueurDensity(target, s.sugar, s.k); const app = apparentAbv(target, s.sugar, s.k);
          return <> · at the {f(target)}% target: {f(d)} kg/m³, a hydrometer would read {app == null ? 'below 0 (use a density meter)' : `${f(app)}% (obscured ${f(target - app)})`}</>;
        })()}
        {unknown.length > 0 && <div style={{ color: '#b45309' }}>Not counted: {unknown.join('; ')}</div>}
      </div>
    );
  }
  if (!(s.lpa > 0) && !(s.sugar > 0) && !unknown.length) return null;
  const diff = target != null && s.lpa > 0 ? s.abv - Number(target) : null;
  const off = diff != null && Math.abs(diff) > 0.3;
  return (
    <div style={{ margin: '6px 6px 0', padding: '6px 10px', fontSize: 12.5, borderRadius: 'var(--radius)', background: off ? '#fff7ed' : 'var(--surface-2)', border: '1px solid var(--border)' }}
      title={s.spirits.map(x => `${x.name}: ${f(x.litres, 3)} L at ${f(x.abv)}%`).join('\n') || undefined}>
      <strong>{label}: {s.lpa > 0 ? `${f(s.abv, 2)}% ABV` : 'no alcohol counted'}</strong>
      {diff != null && <span style={{ marginLeft: 6, fontWeight: 600, color: off ? '#9a3412' : '#166534' }}>{off ? `${diff > 0 ? '+' : '−'}${f(Math.abs(diff), 2)} vs ${f(target)}% target` : `✓ ${f(target)}% target`}</span>}
      {(s.lpa > 0 || s.sugar > 0) && <span style={{ color: 'var(--text-muted)' }}>
        {' · '}{f(s.lpa / s.basis, 3)} LPA per litre
        {s.sugar > 0 && <> · {f(s.sugar, 0)} g/L sugar</>}
        {Number.isFinite(s.density) && <> · {f(s.density)} kg/m³</>}
        {s.sugar > 0 && s.lpa > 0 && <> · hydrometer would read {s.apparent == null ? 'below 0 (use a density meter)' : `${f(s.apparent)}% (obscured ${f(s.abv - s.apparent)})`}</>}
      </span>}
      {s.assumptions.length > 0 && <div style={{ color: 'var(--text-muted)', fontSize: 11.5 }}>Assumed: {s.assumptions.join('; ')}</div>}
      {unknown.length > 0 && <div style={{ color: '#b45309', fontSize: 11.5 }}>Not counted: {unknown.join('; ')}</div>}
    </div>
  );
}

// ── Flavour wheel (Josh, 30 Oct 2026) ─────────────────────────────────────────
// A recipe version's ingredients grouped into flavour categories, as a pie.
// Categories are chosen per recipe (starting from the flavour library), and
// two PDFs come out: internal (g per litre, %) and client (no amounts).
const G_PER = { g: 1, kg: 1000, ml: 1, l: 1000 };
const wheelKey = g => g.stock_item_id || `n:${String(g.name_as_written || '').trim().toLowerCase()}`;
const NOT_ON_WHEEL = /\b(neutral|ngs|grain spirit|alcohol|ethanol|water|base spirit|spirit)\b/i;

/** Each ingredient once, in grams per litre of finished spirit (null when it isn't weighed: units). */
function wheelItems(v) {
  const staged = isStaged(v);
  const { factor } = staged ? stageFactors(v.stages, v.ingredients) : { factor: {} };
  const byKey = new Map();
  for (const g of v.ingredients || []) {
    if (g.from_stage) continue;
    const per = G_PER[String(g.unit || '').toLowerCase()];
    const amt = g.amount != null ? Number(g.amount) : null;
    const perL = per != null && amt != null ? (staged ? amt * (factor[g.stage_number] || 0) : amt / Number(v.basis_litres || 1)) * per : null;
    const k = wheelKey(g);
    const cur = byKey.get(k);
    if (cur) { cur.g = cur.g != null || perL != null ? (cur.g || 0) + (perL || 0) : null; continue; }
    byKey.set(k, { key: k, stock_item_id: g.stock_item_id, name: g.item_name || g.name_as_written || 'Ingredient', g: perL });
  }
  return [...byKey.values()];
}

// A library word in the ingredient's name ("Lemon Peel Dried" → lemon → Citrus); longest word wins.
function byWord(name, words = []) {
  const n = ` ${String(name || '').toLowerCase().replace(/[^a-z ]+/g, ' ')} `;
  const hit = words.filter(w => w.word.length >= 4 && n.includes(` ${w.word.toLowerCase()}`)).sort((a, b) => b.word.length - a.word.length)[0];
  return hit ? hit.category : null;
}

function WheelPreview({ cats, size = 220 }) {
  const total = cats.reduce((s, c) => s + c.value, 0);
  const R = size / 2 - 4; const r = R * 0.46; const c0 = size / 2;
  if (!total) return <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', border: '1px dashed var(--border)', borderRadius: '50%' }}>Put ingredients<br />in categories</div>;
  let a0 = -Math.PI / 2;
  const pt = (rad, a) => `${c0 + rad * Math.cos(a)} ${c0 + rad * Math.sin(a)}`;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Flavour wheel preview">
      {cats.filter(c => c.value > 0).map(c => {
        const a1 = a0 + (c.value / total) * Math.PI * 2; const large = a1 - a0 > Math.PI ? 1 : 0;
        const d = cats.filter(x => x.value > 0).length === 1
          ? `M ${pt(R, 0)} A ${R} ${R} 0 1 1 ${pt(R, Math.PI)} A ${R} ${R} 0 1 1 ${pt(R, 0)} M ${pt(r, 0)} A ${r} ${r} 0 1 0 ${pt(r, Math.PI)} A ${r} ${r} 0 1 0 ${pt(r, 0)} Z`
          : `M ${pt(R, a0)} A ${R} ${R} 0 ${large} 1 ${pt(R, a1)} L ${pt(r, a1)} A ${r} ${r} 0 ${large} 0 ${pt(r, a0)} Z`;
        a0 = a1;
        return <path key={c.name} d={d} fill={c.colour} fillRule="evenodd" stroke="#fff" strokeWidth="1.5"><title>{`${c.name}: ${Math.round((c.value / total) * 100)}%`}</title></path>;
      })}
    </svg>
  );
}

function FlavourWheelModal({ project, version, onClose }) {
  const base = `/recipe-dev/${project.id}/versions/${version.id}/flavour-wheel`;
  const items = wheelItems(version);
  const [state, setState] = useState(null); // { categories, assign, labels, slice_by }
  const [library, setLibrary] = useState([]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    apiFetch(base).then(r => {
      const d = r.data ?? r;
      const from = d.saved || d.earlier || {};
      setLibrary(d.library || []);
      const categories = from.categories?.length ? from.categories : (d.library || []).map(c => ({ name: c.name, colour: c.colour }));
      const assign = {};
      for (const it of items) {
        const known = from.assign && Object.prototype.hasOwnProperty.call(from.assign, it.key) ? from.assign[it.key] : undefined;
        assign[it.key] = known !== undefined ? known
          : (NOT_ON_WHEEL.test(it.name) || (it.g != null && it.g > 300)) ? null
          : (it.stock_item_id && d.defaults?.[it.stock_item_id]) || byWord(it.name, d.words) || null;
        if (assign[it.key] && !categories.some(c => c.name === assign[it.key])) assign[it.key] = null;
      }
      setState({ categories, assign, labels: from.labels || {}, slice_by: from.slice_by || 'weight' });
    }).catch(e => setErr(e.message));
  }, [base]);

  if (!state) return <Modal title="Flavour wheel" onClose={onClose} width={860}>{err ? <Err msg={err} /> : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}</Modal>;

  const setCat = (i, patch) => setState(s => {
    const categories = s.categories.map((c, j) => (j === i ? { ...c, ...patch } : c));
    // Renaming a category keeps its ingredients in it.
    const assign = patch.name !== undefined ? Object.fromEntries(Object.entries(s.assign).map(([k, v]) => [k, v === s.categories[i].name ? patch.name : v])) : s.assign;
    return { ...s, categories, assign };
  });
  const removeCat = i => setState(s => ({ ...s, categories: s.categories.filter((_, j) => j !== i), assign: Object.fromEntries(Object.entries(s.assign).map(([k, v]) => [k, v === s.categories[i].name ? null : v])) }));
  const addCat = () => setState(s => ({ ...s, categories: [...s.categories, { name: `New category ${s.categories.length + 1}`, colour: ['#6d28d9', '#0e7490', '#475569', '#be123c'][s.categories.length % 4] }] }));
  const label = it => state.labels[it.key] || it.name;
  const onWheel = items.filter(it => state.assign[it.key]);
  const value = it => (state.slice_by === 'count' ? 1 : it.g || 0);
  const cats = state.categories.map(c => ({ ...c, value: onWheel.filter(it => state.assign[it.key] === c.name).reduce((s, it) => s + value(it), 0) }));
  const total = cats.reduce((s, c) => s + c.value, 0);
  const off = items.length - onWheel.length;

  async function save() {
    await apiFetch(base, { method: 'PUT', body: state });
  }
  async function download(audience) {
    setBusy(audience); setErr('');
    try {
      await save();
      const res = await fetch(`/api${base}.pdf`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ ...state, audience, version_label: vFull(version), items: onWheel.map(it => ({ label: label(it), category: state.assign[it.key], g_per_l: it.g })) }) });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Couldn't make the PDF (HTTP ${res.status})`); }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = url;
      a.download = `Flavour wheel - ${project.project_name || project.spirit_type || 'recipe'} - ${vLabel(version)}${audience === 'client' ? '' : ' (internal)'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { setErr(e.message); }
    setBusy('');
  }

  const cell = { padding: '5px 6px', borderTop: '1px solid var(--border)', fontSize: 13 };
  return (
    <Modal title={`Flavour wheel — ${vFull(version)}`} onClose={onClose} width={900}>
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20 }}>
        <div>
          <WheelPreview cats={cats} />
          <div style={{ marginTop: 10 }}>
            {cats.filter(c => c.value > 0).map(c => (
              <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 3 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: c.colour, flex: 'none' }} />
                <span style={{ flex: 1 }}>{c.name}</span>
                <span style={{ color: 'var(--text-muted)' }}>{Math.round((c.value / total) * 100)}%</span>
              </div>
            ))}
          </div>
          <div style={{ height: 10 }} />
          <Field label="Slice size by">
            <Select value={state.slice_by} onChange={e => setState(s => ({ ...s, slice_by: e.target.value }))}>
              <option value="weight">Weight (g per litre)</option>
              <option value="count">Number of ingredients</option>
            </Select>
          </Field>
          <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: 0 }}>The client copy never shows amounts or percentages; its slices are always sized by the number of ingredients.</p>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>Categories for this recipe</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {state.categories.map((c, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--border)', borderRadius: 99, padding: '2px 4px 2px 6px', background: 'var(--surface-2)' }}>
                <input type="color" value={c.colour} onChange={e => setCat(i, { colour: e.target.value })} title="Colour" style={{ width: 18, height: 18, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
                <input value={c.name} onChange={e => setCat(i, { name: e.target.value })} style={{ width: Math.max(60, c.name.length * 8 + 6), border: 'none', background: 'transparent', fontSize: 12.5, color: 'var(--text)' }} />
                <button type="button" onClick={() => removeCat(i)} title="Remove" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1 }}>×</button>
              </span>
            ))}
            <Btn size="sm" variant="ghost" type="button" onClick={addCat}>+ Category</Btn>
            {library.length > 0 && <Btn size="sm" variant="ghost" type="button" onClick={() => setState(s => ({ ...s, categories: library.map(c => ({ name: c.name, colour: c.colour })), assign: Object.fromEntries(Object.entries(s.assign).map(([k, v]) => [k, library.some(c => c.name === v) ? v : null])) }))}>Reset to library</Btn>}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>Ingredient (as shown on the wheel)</th>
              <th style={{ textAlign: 'right', padding: '4px 6px', width: 90 }}>g per L</th>
              <th style={{ textAlign: 'left', padding: '4px 6px', width: 210 }}>Category</th>
            </tr></thead>
            <tbody>
              {items.map(it => (
                <tr key={it.key} style={{ opacity: state.assign[it.key] ? 1 : 0.6 }}>
                  <td style={cell}><input value={label(it)} onChange={e => setState(s => ({ ...s, labels: { ...s.labels, [it.key]: e.target.value } }))} style={{ width: '100%', border: '1px solid transparent', background: 'transparent', fontSize: 13, color: 'var(--text)', padding: '3px 4px', borderRadius: 4 }} onFocus={e => (e.target.style.borderColor = 'var(--border)')} onBlur={e => (e.target.style.borderColor = 'transparent')} /></td>
                  <td style={{ ...cell, textAlign: 'right', color: 'var(--text-muted)' }}>{it.g != null ? (it.g >= 10 ? it.g.toFixed(1).replace(/\.0$/, '') : it.g.toFixed(2)) : 'not weighed'}</td>
                  <td style={cell}>
                    <Select value={state.assign[it.key] || ''} onChange={e => setState(s => ({ ...s, assign: { ...s.assign, [it.key]: e.target.value || null } }))}>
                      <option value="">Not on the wheel</option>
                      {state.categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {off > 0 && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>{off} not on the wheel (base spirit and water are left off to start with).</p>}
        </div>
      </div>
      <Err msg={err} />
      <BtnRow>
        <Btn variant="ghost" type="button" onClick={async () => { setBusy('save'); try { await save(); onClose(); } catch (e) { setErr(e.message); setBusy(''); } }} disabled={!!busy}>{busy === 'save' ? 'Saving…' : 'Save and close'}</Btn>
        <Btn variant="ghost" type="button" onClick={() => download('internal')} disabled={!!busy || !onWheel.length}>{busy === 'internal' ? 'Making…' : 'Internal PDF'}</Btn>
        <Btn type="button" onClick={() => download('client')} disabled={!!busy || !onWheel.length}>{busy === 'client' ? 'Making…' : 'Client PDF'}</Btn>
      </BtnRow>
    </Modal>
  );
}

function VersionsPanel({ project, onChanged }) {
  const [mapping, setMapping] = useState(false);
  const [labelFor, setLabelFor] = useState(null); // version (or {}) the sample label dialog is open for
  const [wheelFor, setWheelFor] = useState(null); // version the flavour wheel is open for
  const versions = project.versions ?? [];
  const [params, setParams] = useSearchParams();
  const selId = params.get('version');
  const setSelId = (id) => setParams((p) => { const n = new URLSearchParams(p); if (id) n.set('version', id); else n.delete('version'); return n; }, { replace: true });
  const [scaleTo, setScaleTo] = useState(project.target_bulk_litres ? String(Number(project.target_bulk_litres)) : '1');
  const [editing, setEditing] = useState(null); // { version } | { copyFrom } | { blank: true }
  // Opens on the latest iteration of the latest round (drafts only if there are no rounds).
  const sel = versions.find(v => v.id === selId) || [...versions].reverse().find(v => v.round_number) || versions[versions.length - 1];
  // Compare with the iteration it was tweaked from, else the one before it.
  // Compare with what it came from: the iteration it was tweaked from, else
  // the one before it in its round, else the last of the round before.
  const prev = (() => {
    if (!sel) return null;
    const parent = versions.find(v => v.id === sel.parent_version_id);
    if (parent) return parent;
    const same = versions.filter(v => (v.round_number || null) === (sel.round_number || null));
    const before = same[same.indexOf(sel) - 1];
    if (before) return before;
    if (sel.round_number) return [...versions].reverse().find(v => v.round_number && v.round_number < sel.round_number) || null;
    return null;
  })();
  const groups = [];
  for (const v of versions) {
    const key = v.round_number ? `Round ${v.round_number}` : 'Drafts';
    let g = groups.find(x => x.key === key);
    if (!g) groups.push(g = { key, items: [] });
    g.items.push(v);
  }
  const keyOf = g => `${g.stage_number || (isStaged(sel) ? 1 : 0)}|${g.from_stage ? `f:${g.from_stage}` : g.stock_item_id || `n:${(g.name_as_written || '').toLowerCase()}`}`;
  // Print asks for the batch size first, then opens the printable sheet.
  const [printAsk, setPrintAsk] = useState(null); // { version, litres }
  async function printVersion(v, litres) {
    const win = window.open('', '_blank');
    try {
      const res = await fetch(`/api/recipe-dev/${project.id}/versions/${v.id}/print-html?litres=${encodeURIComponent(litres || '')}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Couldn't open it (HTTP ${res.status})`); }
      const url = URL.createObjectURL(new Blob([await res.text()], { type: 'text/html' }));
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (win) win.close(); uiAlert(e.message); }
  }

  async function makeRecipe(v) {
    const unlinked = v.ingredients.filter(g => !g.from_stage && !g.stock_item_id).map(g => g.name_as_written);
    if (unlinked.length) { uiAlert(`Match these to stock first (Edit or Tweak the iteration): ${unlinked.join(', ')}`); return; }
    if (!await uiConfirm(`Make a production recipe from ${vFull(v)}?\n\nIt's written per 1 L of finished product${isStaged(v) ? `, in ${v.stages.length} stages — each earlier stage's output becomes a semi-product the next stage takes in` : ''}. It starts as a draft on the Recipes page.`, { okLabel: 'Make recipe' })) return;
    try {
      const r = await apiFetch(`/recipe-dev/${project.id}/versions/${v.id}/production-recipe`, { method: 'POST' });
      const d = r.data ?? r;
      uiAlert(`Made "${d.name}" — it's on the Recipes page as a draft.`);
      onChanged();
    } catch (e) { uiAlert(e.message); }
  }

  async function remove(v) {
    if (!await uiConfirm(`Delete ${vFull(v)}? It stays in the record of changes as deleted.`)) return;
    try { await apiFetch(`/recipe-dev/${project.id}/versions/${v.id}`, { method: 'DELETE' }); setSelId(null); onChanged(); } catch (e) { uiAlert(e.message); }
  }
  const scale = Number(scaleTo) > 0 ? Number(scaleTo) : 1;
  let costTotal = 0; let costKnown = 0; let costLines = 0;
  return (
    <div style={{ marginBottom: 24 }}>
      {printAsk && (
        <Modal title={`Print ${vFull(printAsk.version)}`} onClose={() => setPrintAsk(null)} width={400}>
          <form onSubmit={e => { e.preventDefault(); const l = printAsk.litres; setPrintAsk(null); setScaleTo(l); printVersion(printAsk.version, l); }}>
            <Field label="How many litres are you making?">
              <Input type="number" min="0.001" step="any" autoFocus required value={printAsk.litres} onChange={e => setPrintAsk(p => ({ ...p, litres: e.target.value }))} />
            </Field>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
              {[...new Set([Number(printAsk.version.basis_litres), 1, 5, 20, project.target_bulk_litres != null ? Number(project.target_bulk_litres) : null].filter(x => x > 0))].map(x => (
                <button key={x} type="button" onClick={() => setPrintAsk(p => ({ ...p, litres: String(x) }))}
                  style={{ padding: '4px 10px', borderRadius: 99, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: Number(printAsk.litres) === x ? 'var(--pld-green)' : 'var(--surface-2)', color: Number(printAsk.litres) === x ? '#fff' : 'var(--text)' }}>
                  {x.toLocaleString('en-GB')} L{x === Number(project.target_bulk_litres) ? ' (target)' : x === Number(printAsk.version.basis_litres) ? ' (as written)' : ''}
                </button>
              ))}
            </div>
            <BtnRow>
              <Btn variant="ghost" type="button" onClick={() => setPrintAsk(null)}>Cancel</Btn>
              <Btn type="submit" disabled={!(Number(printAsk.litres) > 0)}>Print</Btn>
            </BtnRow>
          </form>
        </Modal>
      )}
      {wheelFor && <FlavourWheelModal project={project} version={wheelFor} onClose={() => setWheelFor(null)} />}
      {labelFor && <SampleLabelModal project={project} version={labelFor.id ? labelFor : null} onClose={() => setLabelFor(null)} />}
      {mapping && <MapRoundsModal project={project} onClose={() => setMapping(false)} onSaved={() => { setMapping(false); onChanged(); }} />}
      {editing && <VersionEditor projectId={project.id} rounds={project.rounds ?? []} version={editing.version} copyFrom={editing.copyFrom}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, flex: 1 }}>Recipe versions</h3>
        {sel && <Btn size="sm" variant="ghost" onClick={() => setMapping(true)}>Link to rounds</Btn>}
        {sel && <Btn size="sm" onClick={() => setEditing({ copyFrom: sel })} title="Copy it into the next iteration and record what you change">Tweak {vLabel(sel)} → new iteration</Btn>}
        <Btn size="sm" variant="ghost" onClick={() => setEditing({ blank: true })}>+ New draft</Btn>
      </div>
      {!sel ? <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No recipe versions yet. Start one here, or import an old development form from the Recipe Development page.</p> : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 14, padding: '8px 10px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', alignItems: 'center' }}>
            {groups.map(g => (
              <div key={g.key} style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginRight: 2 }}>{g.key}</span>
                {g.items.map(v => (
                  <button key={v.id} onClick={() => setSelId(v.id)} title={vFull(v)}
                    style={{ padding: '5px 11px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: v.id === sel.id ? 'var(--pld-green)' : 'var(--surface)', color: v.id === sel.id ? '#fff' : 'var(--text-muted)' }}>
                    {vLabel(v)}{v.sent ? ' ✉' : ''}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div style={{ padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 10 }}>
              <span>
                <strong style={{ color: 'var(--text)', fontSize: 13.5 }}>{vLabel(sel)}</strong>{sel.name ? ` — ${sel.name}` : ''}
                {sel.sent && <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 99, background: '#dbeafe', color: '#1e40af', fontSize: 11, fontWeight: 700 }}>SENT TO CLIENT</span>}
                {sel.locked && !sel.sent && <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 99, background: 'var(--surface-2)', border: '1px solid var(--border)', fontSize: 11, fontWeight: 700 }}>ON RECORD</span>}
                <br />{isStaged(sel) ? `Made in ${sel.stages.length} stages` : `Amounts per ${Number(sel.basis_litres)} L`}{prev ? ` · changes vs ${vLabel(prev)}` : ''}{sel.source === 'import' ? ` · imported from ${sel.source_file || 'a form'}` : ''}
              </span>
              <span style={{ flex: 1 }} />
              <span>Scale to</span>
              <input type="number" min="0" step="any" value={scaleTo} onChange={e => setScaleTo(e.target.value)} style={{ width: 80, padding: '4px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 12.5, background: 'var(--surface)', color: 'var(--text)' }} />
              <span>L</span>
              <CopyLinkButton path={`/recipe-dev?project=${project.id}&version=${sel.id}`} />
              <Btn size="sm" onClick={() => setPrintAsk({ version: sel, litres: scaleTo || String(Number(sel.basis_litres)) })}>Print</Btn>
              <Btn size="sm" variant="ghost" onClick={() => setLabelFor(sel)}>Bottle labels</Btn>
              <Btn size="sm" variant="ghost" onClick={() => setWheelFor(sel)}>Flavour wheel</Btn>
              <Btn size="sm" variant="ghost" onClick={() => setEditing({ version: sel })}
                title={sel.locked ? `${vLabel(sel)} is on the record — you can change its name and notes; tweak it to change the recipe` : undefined}>Edit</Btn>
              {!sel.locked && !sel.production_recipe_id && <Btn size="sm" variant="ghost" onClick={() => remove(sel)}>Delete</Btn>}
              {sel.production_recipe_id
                ? <a href="/recipes" style={{ fontSize: 12, fontWeight: 600, color: 'var(--pld-green)' }}>✓ Production recipe made</a>
                : <Btn size="sm" variant="ghost" onClick={() => makeRecipe(sel)} title="Turn this iteration into a production recipe (per 1 L), stages and all">Make production recipe</Btn>}
            </div>
            {(() => {
              // The tables to show: one per stage, or Ingredients + Added after distilling.
              const staged = isStaged(sel);
              const basisOf = (v, g) => Number((isStaged(v) && v.stages.find(st => st.stage_number === g.stage_number)?.basis_litres) || v.basis_litres || 1);
              const { need, factor } = staged ? stageFactors(sel.stages, sel.ingredients) : { need: {}, factor: {} };
              const strengths = staged ? versionStrength(sel) : null;
              const hasAdditions = !staged && sel.ingredients.some(g => g.section === 'addition');
              const projTarget = project.target_abv != null ? Number(project.target_abv) : null;
              // Cost per litre of each stage's output, built up stage by stage.
              const perL = {};
              if (staged) for (const st of sel.stages) {
                let tot = 0; let known = true;
                for (const g of sel.ingredients.filter(x => x.stage_number === st.stage_number && x.amount != null)) {
                  const c = g.from_stage ? (perL[g.from_stage] != null ? litresOf(g) * perL[g.from_stage] : null) : lineCost(g, Number(g.amount));
                  if (c == null) known = false; else tot += c;
                }
                perL[st.stage_number] = known ? tot / (Number(st.basis_litres) || 1) : null;
              }
              const groups = staged
                ? sel.stages.map(st => ({
                    key: `s${st.stage_number}`, stage: st,
                    title: `Stage ${st.stage_number} — ${st.name}`,
                    sub: [st.target_abv != null ? `${Number(st.target_abv)}% ABV` : null, `make ${niceQty(need[st.stage_number] * scale, 'L')} for ${scale.toLocaleString('en-GB')} L`, perL[st.stage_number] != null ? `£${perL[st.stage_number].toFixed(2)}/L` : null].filter(Boolean).join(' · '),
                    lines: sel.ingredients.filter(g => g.stage_number === st.stage_number),
                    removed: prev ? prev.ingredients.filter(p => (p.stage_number || 1) === st.stage_number && !sel.ingredients.some(g => keyOf(g) === keyOf(p))) : [],
                    basis: Number(st.basis_litres || 1),
                    scaleOf: amt => amt * (factor[st.stage_number] || 0) * scale,
                    method: st.method,
                  }))
                : ['charge', 'addition'].map(secKey => ({
                    key: secKey, title: secKey === 'charge' ? 'Ingredients' : 'Added per litre after distilling',
                    lines: sel.ingredients.filter(g => g.section === secKey),
                    removed: prev ? prev.ingredients.filter(p => p.section === secKey && !sel.ingredients.some(g => keyOf(g) === keyOf(p))) : [],
                    basis: Number(sel.basis_litres || 1),
                    scaleOf: amt => (amt / Number(sel.basis_litres || 1)) * scale,
                  }));
              return groups.map(gr => {
                if (!gr.lines.length && !gr.removed.length) return null;
                return (
                  <div key={gr.key} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: gr.stage ? 'var(--pld-green)' : 'var(--text-muted)', margin: '4px 0' }}>
                      {gr.title}{gr.sub && <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>{gr.sub}</span>}
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
                      <colgroup><col /><col style={{ width: 110 }} /><col style={{ width: 110 }} /><col style={{ width: 110 }} /><col style={{ width: 80 }} /></colgroup>
                      <thead><tr style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
                        <th style={{ textAlign: 'left', padding: '4px 6px' }}>Ingredient</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px' }}>Per {gr.basis} L</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px' }}>Change</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px' }}>For {scale.toLocaleString('en-GB')} L</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px' }}>Cost</th>
                      </tr></thead>
                      <tbody>
                        {gr.lines.map(g => {
                          const p = prev?.ingredients.find(x => keyOf(x) === keyOf(g));
                          const amt = g.amount != null ? Number(g.amount) : null;
                          const scaled = amt != null ? gr.scaleOf(amt) : null;
                          // A stage's own output is costed through it, not counted again.
                          const cost = g.from_stage
                            ? (scaled != null && perL[g.from_stage] != null ? litresOf({ ...g, amount: scaled }) * perL[g.from_stage] : null)
                            : lineCost(g, scaled);
                          if (!g.from_stage) { costLines += 1; if (cost != null) { costTotal += cost; costKnown += 1; } }
                          let change = '';
                          if (prev && !p) change = 'new';
                          else if (p && amt != null && p.amount != null) {
                            // Compare like for like: the previous amount in this version's unit and batch size.
                            const same = g.unit === p.unit ? 1 : convert(1, p.unit, g.unit);
                            if (same != null) {
                              const before = (Number(p.amount) / basisOf(prev, p)) * basisOf(sel, g) * same;
                              if (Math.abs(before - amt) > Math.max(Math.abs(amt), Math.abs(before)) * 0.005) {
                                const shown = Number(before.toPrecision(3));
                                change = `${amt > before ? '▲' : '▼'} from ${shown}`;
                              }
                            }
                          }
                          const fromSt = g.from_stage ? sel.stages.find(st => st.stage_number === g.from_stage) : null;
                          return (
                            <tr key={g.id} style={{ borderTop: '1px solid var(--border)' }}>
                              <td style={{ padding: '5px 6px' }}>
                                {fromSt ? <span>↳ {fromSt.name} <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>(from stage {fromSt.stage_number})</span></span> : (g.item_name || g.name_as_written)}
                                {!g.stock_item_id && !g.from_stage && <span style={{ marginLeft: 6, fontSize: 10.5, color: '#b45309' }}>not in stock</span>}
                                {!fromSt && g.item_name && g.name_as_written && g.item_name.toLowerCase() !== g.name_as_written.toLowerCase() && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>("{g.name_as_written}")</span>}
                              </td>
                              <td style={{ padding: '5px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{amt != null ? `${amt} ${g.unit || '?'}` : '—'}</td>
                              <td style={{ padding: '5px 6px', textAlign: 'right', fontSize: 12, color: change === 'new' ? '#2563eb' : change.startsWith('▲') ? '#b45309' : change ? '#2563eb' : 'var(--text-muted)' }}>{change}</td>
                              <td style={{ padding: '5px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{g.unit ? niceQty(scaled, g.unit) : '—'}</td>
                              <td style={{ padding: '5px 6px', textAlign: 'right', color: 'var(--text-muted)', fontStyle: g.from_stage ? 'italic' : undefined }}>{cost != null ? `£${cost.toFixed(2)}` : ''}</td>
                            </tr>
                          );
                        })}
                        {gr.removed.map(p => (
                          <tr key={`rm-${p.id}`} style={{ borderTop: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                            <td style={{ padding: '5px 6px', textDecoration: 'line-through' }}>{p.from_stage ? `Stage ${p.from_stage} output` : (p.item_name || p.name_as_written)}</td>
                            <td /><td style={{ padding: '5px 6px', textAlign: 'right', fontSize: 12, color: '#dc2626' }}>removed</td><td /><td />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {gr.stage
                      ? <StrengthLine s={strengths[gr.stage.stage_number]} label={`Stage ${gr.stage.stage_number} predicted`}
                          target={gr.stage.target_abv != null ? Number(gr.stage.target_abv) : (gr.stage.stage_number === Math.max(...sel.stages.map(x => x.stage_number)) ? projTarget : null)} />
                      : gr.key === 'charge'
                        ? <StrengthLine s={stageStrength(gr.lines, gr.basis)} label={hasAdditions ? 'Still charge' : 'Predicted'} target={hasAdditions ? null : projTarget} />
                        : <StrengthLine s={stageStrength(gr.lines, 1)} additions target={projTarget} />}
                    {gr.method && <p style={{ margin: '4px 6px 0', fontSize: 12.5, whiteSpace: 'pre-line', color: 'var(--text-muted)' }}>{gr.method}</p>}
                  </div>
                );
              });
            })()}
            {costKnown > 0 && (
              <p style={{ margin: '4px 0 8px', fontSize: 12.5 }}>
                <strong>Ingredient cost for {scale.toLocaleString('en-GB')} L: £{costTotal.toFixed(2)}</strong> (£{(costTotal / scale).toFixed(2)}/L){costKnown < costLines ? <span style={{ color: 'var(--text-muted)' }}> — {costLines - costKnown} ingredient{costLines - costKnown === 1 ? ' has' : 's have'} no price yet</span> : null}
              </p>
            )}
            {sel.method && <p style={{ margin: '6px 0 0', fontSize: 13, whiteSpace: 'pre-line' }}><strong>Method:</strong> {sel.method}</p>}
            {sel.notes && <p style={{ margin: '6px 0 0', fontSize: 13, whiteSpace: 'pre-line', color: 'var(--text-muted)' }}>{sel.notes}</p>}
          </div>
        </div>
      )}
      <ChangeRecord changes={project.version_changes ?? []} />
    </div>
  );
}

// ── Import an old development form ───────────────────────────────────────────
const NEW_UNIT = { g: 'kg', kg: 'kg', mg: 'kg', ml: 'litres', L: 'litres', units: 'units', drops: 'litres' };
const NEW_CATS = [['raw_material', 'Raw material'], ['semi_product', 'Semi product'], ['consumable', 'Consumable'], ['packaging', 'Packaging']];

function ImportFormModal({ onClose, onDone }) {
  const { data: clientData, refetch: refetchClients } = useApi('/clients');
  const clients = clientData?.clients ?? clientData ?? [];
  const [newClient, setNewClient] = useState(null); // name being typed, or null
  const [clientForm, setClientForm] = useState(false); // importing a customer form
  async function addClient() {
    const name = (newClient || '').trim();
    if (!name) return;
    setErr('');
    try {
      const existing = clients.find(c => c.name.toLowerCase() === name.toLowerCase());
      const c = existing || await apiFetch('/clients', { method: 'POST', body: { name } });
      await refetchClients();
      setTarget(t => ({ ...t, client_id: c.id || c.client?.id }));
      setNewClient(null);
    } catch (e) { setErr(e.message); }
  }
  const { data: projData } = useApi('/recipe-dev');
  const projects = projData?.projects ?? projData ?? [];
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [include, setInclude] = useState({});
  const [names, setNames] = useState({});
  const [map, setMap] = useState({});   // ingredient name -> { mode: 'stock'|'create'|'skip'|'none', stock_item_id, label, create: {...} }
  const [units, setUnits] = useState({}); // ingredient name -> unit for lines with none
  const [bases, setBases] = useState({}); // version index -> litres the amounts are for
  const [roundNums, setRoundNums] = useState({}); // version index -> sample round it was sent in
  const [target, setTarget] = useState({ mode: 'new', project_id: '', client_id: '', project_name: '', spirit_type: 'gin' });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');

  async function downloadTemplate() {
    try {
      const res = await fetch('/api/recipe-dev/import/template', { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(`Couldn't download it (HTTP ${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = url; a.download = 'Recipe Development Form (Blake).xlsx'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { setErr(e.message); }
  }

  async function read() {
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch('/api/recipe-dev/import/parse', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Couldn't read it (HTTP ${res.status})`);
      const p = body.data;
      setParsed(p);
      setInclude(Object.fromEntries(p.versions.map((_, i) => [i, true])));
      setNames(Object.fromEntries(p.versions.map((v, i) => [i, v.name])));
      const m = {};
      for (const [name, r] of Object.entries(p.matches)) {
        const c = r.candidates.find(x => x.id === r.stock_item_id);
        m[name] = r.stock_item_id ? { mode: 'stock', stock_item_id: r.stock_item_id, label: c?.name, how: r.matched_by } : { mode: 'create' };
        const unitUsed = p.versions.flatMap(v => v.ingredients).find(g => g.name === name && g.unit)?.unit;
        m[name].create = { name: name.replace(/^dried\s+/i, '').replace(/\b\w/g, ch => ch.toUpperCase()), category: 'raw_material', unit: NEW_UNIT[unitUsed] || 'kg' };
      }
      setMap(m);
      const noUnit = [...new Set(p.versions.flatMap(v => v.ingredients).filter(g => !g.unit && g.amount != null).map(g => g.name))];
      setUnits(Object.fromEntries(noUnit.map(n => [n, 'g'])));
      setBases(Object.fromEntries(p.versions.map((v, i) => [i, String(v.basis_litres)])));
      const spiritFromName = ((p.project_name || '').match(/\b(gin|vodka|rum|whisky|whiskey|brandy|liqueur|tequila|aquavit|absinthe)\b/i) || [])[1];
      setTarget(t => ({ ...t, project_name: p.project_name || '', client_id: p.client_id || t.client_id, spirit_type: p.spirit_type || (spiritFromName ? spiritFromName.toLowerCase() : t.spirit_type) }));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  async function commit() {
    setBusy(true); setErr('');
    try {
      const versions = parsed.versions.filter((_, i) => include[i]).map(v => ({
        ...v, name: names[parsed.versions.indexOf(v)] || v.name, basis_litres: v.stages?.length >= 2 ? v.basis_litres : (Number(bases[parsed.versions.indexOf(v)]) || v.basis_litres), round_number: Number(roundNums[parsed.versions.indexOf(v)]) || null,
        ingredients: v.ingredients.map(g => ({ ...g, unit: g.unit || (g.amount != null ? units[g.name] : null) })),
      }));
      if (!versions.length) throw new Error('Tick at least one version');
      const mapping = {};
      for (const [name, m] of Object.entries(map)) {
        if (m.mode === 'stock' && m.stock_item_id) mapping[name] = { stock_item_id: m.stock_item_id };
        else if (m.mode === 'create') mapping[name] = { create: m.create };
        else if (m.mode === 'skip') mapping[name] = { skip: true };
      }
      const body = { versions, mapping, source_file: file.name };
      if (target.mode === 'existing') {
        if (!target.project_id) throw new Error('Pick the project');
        body.project_id = target.project_id;
      } else {
        if (!target.client_id) throw new Error('Pick the client');
        body.new_project = { client_id: target.client_id, project_name: target.project_name, spirit_type: target.spirit_type || 'gin' };
      }
      const res = await apiFetch('/recipe-dev/import/commit', { method: 'POST', body });
      const projectId = res.data?.project_id;
      // Keep the original form with the project.
      try {
        const fd = new FormData(); fd.append('file', file); fd.append('doc_type', 'spec'); fd.append('title', `Development form — ${file.name}`);
        await fetch(`/api/recipe-dev/${projectId}/documents`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      } catch { /* the versions are what matter */ }
      onDone(projectId, res.data?.created_items || []);
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  const ingNames = parsed ? Object.keys(parsed.matches) : [];
  const usedIn = n => parsed.versions.filter((v, i) => include[i] && v.ingredients.some(g => g.name === n && !g.from_stage)).length;
  return (
    <Modal title="Import a development form" onClose={onClose} width={parsed ? 900 : 520}>
      {clientForm && (
        <ImportCustomerFormModal
          onClose={() => setClientForm(false)}
          onSaved={async (c) => { setClientForm(false); await refetchClients(); setTarget(t => ({ ...t, client_id: c.id })); }}
          onOpenExisting={(c) => { setClientForm(false); setTarget(t => ({ ...t, client_id: c.id })); }}
        />
      )}
      <Err msg={err} />
      {!parsed ? (
        <>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>Choose a recipe development spreadsheet (.xlsx). Blake reads each tab as a recipe version (a tab with two recipe tables becomes two versions) and matches the ingredients to stock. Nothing is saved until you've checked it.</p>
          <p style={{ margin: '0 0 12px', fontSize: 13 }}>Starting a new one? <button type="button" onClick={downloadTemplate} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pld-green)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>Download the Blake template</button> — it imports cleanly every time.</p>
          <Field label="Development form"><input type="file" accept=".xlsx" onChange={e => setFile(e.target.files?.[0] ?? null)} /></Field>
          <BtnRow>
            <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
            <Btn type="button" onClick={read} disabled={!file || busy}>{busy ? 'Reading…' : 'Read form'}</Btn>
          </BtnRow>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {[['new', 'New project'], ['existing', 'Add to an existing project']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setTarget(t => ({ ...t, mode: k }))} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: target.mode === k ? 'var(--pld-green)' : 'var(--surface-2)', color: target.mode === k ? '#fff' : 'var(--text-muted)' }}>{l}</button>
            ))}
          </div>
          {target.mode === 'new' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px', gap: 10 }}>
              <Field label="Client *">
                {newClient === null ? (
                  <Select value={target.client_id} onChange={e => {
                    if (e.target.value === '__new') setNewClient(parsed.client_name && !parsed.client_id ? parsed.client_name : '');
                    else if (e.target.value === '__form') setClientForm(true);
                    else setTarget(t => ({ ...t, client_id: e.target.value }));
                  }}>
                    <option value="">Select client…</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    <option value="__new">+ New client…</option>
                    <option value="__form">+ New client from their customer form…</option>
                  </Select>
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Input autoFocus value={newClient} onChange={e => setNewClient(e.target.value)} placeholder="New client's name"
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addClient(); } if (e.key === 'Escape') setNewClient(null); }} />
                    <Btn type="button" size="sm" onClick={addClient} disabled={!newClient.trim()}>Add</Btn>
                    <Btn type="button" size="sm" variant="ghost" onClick={() => setNewClient(null)}>Cancel</Btn>
                  </div>
                )}
                {newClient === null && parsed.client_name && !parsed.client_id && !target.client_id && (
                  <span style={{ fontSize: 11.5, color: '#b45309' }}>The form says "{parsed.client_name}" — not in Blake yet. <button type="button" onClick={() => setNewClient(parsed.client_name)} style={{ background: 'none', border: 'none', padding: 0, color: '#b45309', textDecoration: 'underline', cursor: 'pointer', fontSize: 11.5 }}>Add them</button></span>
                )}
              </Field>
              <Field label="Project name"><Input value={target.project_name} onChange={e => setTarget(t => ({ ...t, project_name: e.target.value }))} /></Field>
              <Field label="Spirit type"><Input value={target.spirit_type} onChange={e => setTarget(t => ({ ...t, spirit_type: e.target.value }))} /></Field>
            </div>
          ) : (
            <Field label="Project *"><Select value={target.project_id} onChange={e => setTarget(t => ({ ...t, project_id: e.target.value }))}><option value="">Select project…</option>{projects.map(p => <option key={p.id} value={p.id}>{p.project_ref} — {p.project_name || p.spirit_type} ({p.client_name})</option>)}</Select></Field>
          )}

          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', margin: '6px 0' }}>Versions found ({parsed.versions.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
            {parsed.versions.map((v, i) => (
              <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={!!include[i]} onChange={e => setInclude(x => ({ ...x, [i]: e.target.checked }))} />
                <Input value={names[i] || ''} onChange={e => setNames(x => ({ ...x, [i]: e.target.value }))} style={{ maxWidth: 240, padding: '4px 8px', fontSize: 12.5 }} />
                {v.stages?.length >= 2 ? (
                  <span style={{ fontSize: 12, color: 'var(--pld-green)', fontWeight: 600, whiteSpace: 'nowrap' }} title={v.stages.map(st => `Stage ${st.stage_number}: ${st.name} (per ${st.basis_litres} L${st.target_abv != null ? `, ${st.target_abv}%` : ''})`).join('\n')}>
                    {v.stages.length} stages: {v.stages.map(st => st.name).join(' → ')}
                  </span>
                ) : (<>
                <span style={{ fontSize: 12, color: v.basis_guessed ? '#b45309' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>amounts for</span>
                <Input type="number" min="0.001" step="any" value={bases[i] ?? ''} onChange={e => setBases(x => ({ ...x, [i]: e.target.value }))}
                  title={v.basis_guessed ? 'The form doesn\'t say — guessed from the total liquid. Check it.' : undefined}
                  style={{ width: 70, padding: '4px 6px', fontSize: 12.5, borderColor: v.basis_guessed ? '#d97706' : undefined }} />
                <span style={{ fontSize: 12, color: v.basis_guessed ? '#b45309' : 'var(--text-muted)' }}>L{v.basis_guessed ? ' (guessed — check)' : ''}</span>
                </>)}
                <select value={roundNums[i] || ''} onChange={e => setRoundNums(x => ({ ...x, [i]: e.target.value }))} title="The sample round this version was sent in" style={{ padding: '4px 6px', fontSize: 12, borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)' }}>
                  <option value="">No round</option><option value="1">Round 1</option><option value="2">Round 2</option><option value="3">Round 3</option>
                </select>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>tab "{v.sheet}" · {v.ingredients.length} ingredients{v.notes ? ` · ${v.notes.split('\n')[0]}` : ''}</span>
              </label>
            ))}
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', margin: '6px 0' }}>Ingredients — match to stock</div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', maxHeight: 360, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead><tr style={{ background: 'var(--surface-2)', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', position: 'sticky', top: 0 }}>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>On the form</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', width: 170 }}>Action</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Stock item</th>
              </tr></thead>
              <tbody>
                {ingNames.filter(n => usedIn(n) > 0).map(n => {
                  const m = map[n] || { mode: 'none' };
                  const r = parsed.matches[n];
                  const setM = patch => setMap(x => ({ ...x, [n]: { ...x[n], ...patch } }));
                  return (
                    <tr key={n} style={{ borderTop: '1px solid var(--border)', verticalAlign: 'top' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <strong>{n}</strong> <span style={{ color: 'var(--text-muted)' }}>· {usedIn(n)} version{usedIn(n) === 1 ? '' : 's'}</span>
                        {n in units && (
                          <div style={{ marginTop: 4, fontSize: 11.5, color: '#b45309' }}>No unit on the form —
                            <select value={units[n]} onChange={e => setUnits(u => ({ ...u, [n]: e.target.value }))} style={{ marginLeft: 4, fontSize: 11.5 }}>{UNITS.map(u => <option key={u} value={u}>{u}</option>)}</select>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={m.mode} onChange={e => setM({ mode: e.target.value })} style={{ width: '100%', padding: 4, fontSize: 12 }}>
                          <option value="stock">Use stock item</option>
                          <option value="create">Create new material</option>
                          <option value="none">Keep the name only</option>
                          <option value="skip">Leave it out</option>
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        {m.mode === 'stock' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {r.candidates.length > 0 && (
                              <select value={m.stock_item_id || ''} onChange={e => { const c = r.candidates.find(x => x.id === e.target.value); setM({ stock_item_id: e.target.value || null, label: c?.name, how: null }); }} style={{ padding: 4, fontSize: 12 }}>
                                <option value="">— choose —</option>
                                {r.candidates.map(c => <option key={c.id} value={c.id}>{c.name} ({c.sku})</option>)}
                                {m.stock_item_id && !r.candidates.some(c => c.id === m.stock_item_id) && <option value={m.stock_item_id}>{m.label}</option>}
                              </select>
                            )}
                            <StockPicker value={m.stock_item_id} label={`Search: ${m.label || ''}`} placeholder="Search all stock…" onPick={si => setM({ stock_item_id: si.id, label: si.name, how: null })} />
                            {m.how && <span style={{ fontSize: 11, color: m.how === 'best guess' ? '#b45309' : 'var(--text-muted)' }}>{m.how === 'best guess' ? 'Best guess — check it' : m.how === 'remembered' ? 'Remembered from an earlier import' : `Matched by ${m.how}`}</span>}
                          </div>
                        )}
                        {m.mode === 'create' && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 110px 80px', gap: 4 }}>
                            <Input value={m.create.name} onChange={e => setM({ create: { ...m.create, name: e.target.value } })} style={{ padding: '4px 8px', fontSize: 12 }} />
                            <select value={m.create.category} onChange={e => setM({ create: { ...m.create, category: e.target.value } })} style={{ padding: 4, fontSize: 12 }}>{NEW_CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                            <select value={m.create.unit} onChange={e => setM({ create: { ...m.create, unit: e.target.value } })} style={{ padding: 4, fontSize: 12 }}>{['kg', 'litres', 'units'].map(u => <option key={u} value={u}>{u}</option>)}</select>
                          </div>
                        )}
                        {m.mode === 'none' && <span style={{ color: 'var(--text-muted)' }}>Kept as "{n}", not linked to stock</span>}
                        {m.mode === 'skip' && <span style={{ color: 'var(--text-muted)' }}>Not imported</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>Rounds picked above that aren't in Blake yet are added (not marked as sent) — add dates and verdicts on the project afterwards. </p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>Blake remembers these matches, so the next form with the same names matches itself. New materials are added to stock with no quantity.</p>
          <BtnRow>
            <Btn variant="ghost" type="button" onClick={() => setParsed(null)}>Back</Btn>
            <Btn type="button" onClick={commit} disabled={busy}>{busy ? 'Importing…' : `Import ${Object.values(include).filter(Boolean).length} version${Object.values(include).filter(Boolean).length === 1 ? '' : 's'}`}</Btn>
          </BtnRow>
        </>
      )}
    </Modal>
  );
}

// ── Project detail ────────────────────────────────────────────────────────────
// Must match recipeDevelopmentService PROJECT_TRANSITIONS.
const STATUS_LABEL = { briefed: 'Briefed', in_development: 'In development', round_1: 'Round 1', round_2: 'Round 2', round_3: 'Round 3', approved: 'Approved', cancelled: 'Cancelled',
  dispatched: 'Sent to client', pass: 'Internal pass', conditional: 'Conditional', fail: 'Internal fail', revise: 'Client: revise', rejected: 'Client: rejected' };
const NEXT_STATUSES = {
  briefed: ['in_development', 'cancelled'],
  in_development: ['round_1', 'cancelled'],
  round_1: ['round_2', 'approved', 'cancelled'],
  round_2: ['round_3', 'approved', 'cancelled'],
  round_3: ['approved', 'cancelled'],
  approved: [], cancelled: [],
};

function ProjectDetail({ projectId, onBack, onRefresh }) {
  const { data, loading, refetch } = useApi(`/recipe-dev/${projectId}`, [projectId]);
  const project = data ?? null;
  const [modal, setModal] = useState(null);
  async function unsend(r) {
    if (!(await uiConfirm(`Unsend round ${r.round_number}? It goes back to not sent — the date, where it went from and which recipe went are cleared, and its sample parcel is cancelled if it's still waiting for a DPD file.`, { okLabel: 'Unsend' }))) return;
    try {
      const res = await apiFetch(`/recipe-dev/${projectId}/rounds/${r.id}/unsend`, { method: 'POST', body: {} });
      refetch();
      const note = (res?.data ?? res)?.note;
      if (note) uiAlert(note);
    } catch (e) { uiAlert(e.message); }
  }
  const [actErr, setActErr] = useState('');
  const [tab, setTab] = useState('overview');

  async function updateStatus(status) {
    try { await apiFetch(`/recipe-dev/${projectId}/status`, { method: 'PATCH', body: { status } }); refetch(); onRefresh(); }
    catch(e) { setActErr(e.message); }
  }

  async function linkVersion(versionId, roundId) {
    try { await apiFetch(`/recipe-dev/${projectId}/versions/${versionId}/round`, { method: 'PUT', body: { round_id: roundId } }); refetch(); }
    catch (e) { setActErr(e.message); }
  }

  async function convertToRun() {
    if (!await uiConfirm('Convert this approved project to a production run?')) return;
    try { const r = await apiFetch(`/recipe-dev/${projectId}/convert`, { method: 'POST', body: {} }); refetch(); onRefresh(); uiAlert(`Converted! Run ref: ${r?.data?.run_ref || 'see Production'}`); }
    catch(e) { setActErr(e.message); }
  }

  if (loading || !project) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading...</div>;

  const rounds = project.rounds ?? [];
  // The server only adds a round once development has started (see createRound).
  const canAddRound = rounds.length < 3 && ['in_development', 'round_1', 'round_2'].includes(project.status);

  return (
    <div>
      {modal?.type === 'round'    && <CreateRoundModal projectId={projectId} roundNumber={rounds.length + 1} versions={project.versions ?? []} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); }} />}
      {modal?.type === 'dispatch' && <DispatchModal projectId={projectId} roundId={modal.roundId} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); }} />}
      {modal?.type === 'approver' && <VerdictModal projectId={projectId} roundId={modal.roundId} type="approver" onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); }} />}
      {modal?.type === 'client'   && <VerdictModal projectId={projectId} roundId={modal.roundId} type="client" onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); onRefresh(); }} />}
      {modal?.type === 'targets'  && <TargetsModal projectId={projectId} targets={project.creative?.targets ?? []} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); }} />}
      {modal?.type === 'tasting'  && <TastingModal projectId={projectId} round={modal.round} targets={project.creative?.targets ?? []} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); }} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0, marginTop: 4 }}>&#8592;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{project.project_name || `${project.client_name} — ${project.spirit_type}`}</h2>
            <StatusBadge status={project.status} />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{project.client_name} · {project.spirit_type || 'spirit type TBC'} · Created {fmt(project.created_at)}</p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {project.status !== 'approved' && project.status !== 'converted' && project.status !== 'cancelled' && (
            <select onChange={e => { if (e.target.value) updateStatus(e.target.value); e.target.value = ''; }} style={{ padding: '5px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 12, background: 'var(--surface-2)', color: 'var(--text)' }}>
              <option value="">Update status...</option>
              {(NEXT_STATUSES[project.status] || []).map(s => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
            </select>
          )}
          {project.status === 'approved' && <Btn size="sm" onClick={convertToRun}>Convert to Run</Btn>}
          {canAddRound && <Btn size="sm" variant="ghost" onClick={() => setModal({ type: 'round' })}>+ Round {rounds.length + 1}</Btn>}
          {project.status === 'briefed' && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Change the status to In development to start sample rounds.</span>}
        </div>
      </div>

      {actErr && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{actErr}</p>}

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 18 }}>
        {[['overview', 'Brief & rounds'], ['documents', `Documents${project.documents?.length ? ` (${project.documents.length})` : ''}`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none', borderBottom: `2px solid ${tab === k ? 'var(--pld-green)' : 'transparent'}`, color: tab === k ? 'var(--text)' : 'var(--text-muted)', marginBottom: -1 }}>{l}</button>
        ))}
      </div>

      {tab === 'documents' ? (
        <DocumentsPanel projectId={projectId} documents={project.documents ?? []} rounds={rounds} onChanged={refetch} />
      ) : (<>

      {/* Brief */}
      {project.brief && <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', fontSize: 13, marginBottom: 20 }}><strong style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>Brief</strong><p style={{ margin: '6px 0 0', lineHeight: 1.6 }}>{project.brief}</p></div>}

      <TargetsPanel project={project} onEdit={() => setModal({ type: 'targets' })} />

      {/* Info grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Target ABV', value: project.target_abv ? `${fmtAbv(project.target_abv)}%` : '—' },
          { label: 'Bottle size', value: project.target_bottle_size_ml ? `${project.target_bottle_size_ml}ml` : '—' },
          { label: 'Target bulk litres', value: project.target_bulk_litres != null ? `${Number(project.target_bulk_litres).toLocaleString('en-GB')} L` : '—' },
          { label: 'Development fee', value: project.development_fee ? `£${Number(project.development_fee).toFixed(2)}` : '—' },
          { label: 'Rounds', value: `${rounds.length} / 3` },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>
          </div>
        ))}
      </div>

      <VersionsPanel project={project} onChanged={refetch} />

      {/* Rounds */}
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Development Rounds</h3>
      {rounds.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No rounds yet. Add Round 1 above{(project.versions ?? []).length ? ', or use "Link to rounds" on the versions to record rounds already sent' : ''}.</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rounds.map((r, i) => (
            <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>Round {r.round_number || (i + 1)}</span>
                  <StatusBadge status={r.client_verdict || r.approver_verdict || (r.sample_dispatched_at ? 'dispatched' : 'in_development')} />
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Btn size="sm" variant="ghost" onClick={() => setModal({ type: 'tasting', round: r })}>+ Tasting</Btn>
                  {!r.approver_verdict && <Btn size="sm" variant="ghost" onClick={() => setModal({ type: 'approver', roundId: r.id })}>Internal verdict</Btn>}
                  {!r.sample_dispatched_at && <Btn size="sm" variant="ghost" onClick={() => setModal({ type: 'dispatch', roundId: r.id })}>Sent to client</Btn>}
                  {(r.sample_dispatched_at || r.sent_dev_version_id) && !r.client_verdict && <Btn size="sm" variant="ghost" onClick={() => unsend(r)}>Unsend</Btn>}
                  {!r.client_verdict && <Btn size="sm" variant="ghost" onClick={() => setModal({ type: 'client', roundId: r.id })}>Client verdict</Btn>}
                </div>
              </div>
              <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, fontSize: 13 }}>
                <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>MADE IN</span><div style={{ fontWeight: 600, marginTop: 2 }}>{(r.produced_at || '—').toUpperCase()}</div></div>
                {r.sample_abv != null && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>SAMPLE ABV</span><div style={{ fontWeight: 600, marginTop: 2 }}>{fmtAbv(r.sample_abv)}%</div></div>}
                {r.batch_volume_litres != null && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>BATCH VOLUME</span><div style={{ fontWeight: 600, marginTop: 2 }}>{Number(r.batch_volume_litres)} L</div></div>}
                {r.sample_dispatched_at && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>SENT</span><div style={{ fontWeight: 600, marginTop: 2 }}>{fmt(r.sample_dispatched_at)}{r.sample_dispatched_from ? ` from ${r.sample_dispatched_from.toUpperCase()}` : ''}</div></div>}
                {r.approver_verdict && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>INTERNAL</span><div style={{ marginTop: 2 }}><StatusBadge status={r.approver_verdict} /></div></div>}
                {r.client_verdict && <div><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>CLIENT</span><div style={{ marginTop: 2 }}><StatusBadge status={r.client_verdict} /></div></div>}
              </div>
              {(project.versions ?? []).length > 0 && (
                <div style={{ padding: '0 16px 10px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>RECIPE</span>
                  {(project.versions ?? []).filter(v => v.round_id === r.id).map(v => (
                    <span key={v.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 4px 2px 10px', borderRadius: 99, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                      {vLabel(v)}{v.sent ? ' (sent)' : ''}
                      <button title="Unlink" onClick={() => linkVersion(v.id, null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1, padding: '0 4px' }}>×</button>
                    </span>
                  ))}
                  <select value="" onChange={e => { if (e.target.value) linkVersion(e.target.value, r.id); }} style={{ padding: '3px 6px', fontSize: 12, borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)' }}>
                    <option value="">{(project.versions ?? []).some(v => v.round_id === r.id) ? '+ add another…' : 'Link a recipe version…'}</option>
                    {(project.versions ?? []).filter(v => v.round_id !== r.id).map(v => <option key={v.id} value={v.id}>{vFull(v)}</option>)}
                  </select>
                </div>
              )}
              {(r.notes || r.approver_tasting_notes || r.client_feedback_raw) && (
                <div style={{ padding: '0 16px 12px', fontSize: 13, color: 'var(--text-muted)' }}>
                  {r.notes && <p style={{ margin: '0 0 4px' }}><strong>Recipe notes:</strong> {r.notes}</p>}
                  {r.approver_tasting_notes && <p style={{ margin: '0 0 4px' }}><strong>Internal tasting:</strong> {r.approver_tasting_notes}</p>}
                  {r.client_feedback_raw && <p style={{ margin: 0 }}><strong>Client feedback:</strong> {r.client_feedback_raw}</p>}
                </div>
              )}
              <RoundScore projectId={projectId} score={project.creative?.rounds?.[r.id]} onChanged={refetch} />
            </div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}

// ── Main list ─────────────────────────────────────────────────────────────────
const STATUSES = ['briefed', 'in_development', 'round_1', 'round_2', 'round_3', 'approved', 'cancelled'];

// ── Save a sandbox blend to a project as a new iteration (Joe, 27 Oct 2026) ──
function SaveBlendModal({ blend, projects, onClose, onSaved }) {
  const open = projects.filter(p => !['completed', 'cancelled', 'archived'].includes(p.status));
  const [projectId, setProjectId] = useState(open[0]?.id || projects[0]?.id || '');
  const [name, setName] = useState('Sandbox blend');
  const [syrup, setSyrup] = useState({ id: null, label: '', name: 'Syrup' });
  const [spirit, setSpirit] = useState({ id: null, label: '', name: `Spirit ${blend.spiritAbv}%` });
  const [water, setWater] = useState({ id: null, label: '', name: 'Water' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const r = blend.result;
  const basis = Math.round(r.volume * 1000) / 1000;
  async function save() {
    if (!projectId) { setErr('Choose a project'); return; }
    if (!(basis > 0)) { setErr('The blend has no volume'); return; }
    setSaving(true); setErr('');
    const line = (who, amt) => ({ stock_item_id: who.id || null, name_as_written: who.id ? who.label : (who.name.trim() || null), amount: amt.amount, unit: amt.unit });
    const ingredients = [line(syrup, blend.syrup), line(spirit, blend.spirit), ...(blend.water ? [line(water, blend.water)] : [])];
    const notes = `From the sandbox: ${blend.sheet} Syrup ≈ ${Math.round(blend.syrupSugar)} g/L sugar from its density. Expected ${r.abv.toFixed(2)}% vol, ~${Math.round(r.sugar)} g/L sugar.`;
    try {
      const res = await apiFetch(`/recipe-dev/${projectId}/versions`, { method: 'POST', body: { name, basis_litres: basis, notes, ingredients } });
      onSaved(projectId, (res?.data ?? res)?.id);
    } catch (e) { setErr(e.message); setSaving(false); }
  }
  const who = (v, set, hint) => (
    <>
      <StockPicker value={v.id} label={v.label} placeholder={v.id ? v.label : `Pick from stock (${hint})…`} onPick={si => set({ ...v, id: si.id, label: si.name })} />
      {v.id ? <button type="button" onClick={() => set({ ...v, id: null, label: '' })} style={{ background: 'none', border: 'none', padding: 0, marginTop: 4, fontSize: 12, color: 'var(--text-muted)', textDecoration: 'underline', cursor: 'pointer', alignSelf: 'flex-start' }}>Type a name instead</button>
        : <Input value={v.name} onChange={e => set({ ...v, name: e.target.value })} placeholder="…or type a name" style={{ marginTop: 6 }} />}
    </>
  );
  const amt = (a) => `${a.amount} ${a.unit}`;
  return (
    <Modal title="Save to project" onClose={onClose} width={560}>
      {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 14px' }}>
        Adds a new iteration to the project, made per {basis} L: {amt(blend.syrup)} syrup, {amt(blend.spirit)} spirit{blend.water ? `, ${amt(blend.water)} water` : ''}. Expected {r.abv.toFixed(2)}% vol and ~{Math.round(r.sugar)} g/L sugar.
      </p>
      <Field label="Project">
        <Select value={projectId} onChange={e => setProjectId(e.target.value)}>
          {projects.length === 0 && <option value="">No projects yet</option>}
          {projects.map(p => <option key={p.id} value={p.id}>{p.project_ref ? `${p.project_ref} — ` : ''}{p.project_name || p.spirit_type}{p.client_name ? ` (${p.client_name})` : ''}</option>)}
        </Select>
      </Field>
      <Field label="Iteration name"><Input value={name} onChange={e => setName(e.target.value)} /></Field>
      <Field label={`Syrup — ${amt(blend.syrup)}`}>{who(syrup, setSyrup, 'set its sugar % and density under Composition for predictions')}</Field>
      <Field label={`Spirit — ${amt(blend.spirit)} at ${blend.spiritAbv}% vol`}>{who(spirit, setSpirit, 'e.g. Neutral Grain Spirit')}</Field>
      {blend.water && <Field label={`Water — ${amt(blend.water)}`}>{who(water, setWater, 'optional')}</Field>}
      <BtnRow>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving || !projectId}>{saving ? 'Saving…' : 'Save iteration'}</Btn>
      </BtnRow>
    </Modal>
  );
}

export default function RecipeDev() {
  const [statusFilter, setStatusFilter] = useState('all');
  // The open project (and iteration) are in the address, so a copied link opens them.
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('project');
  const setSelectedId = (id) => setParams(id ? { project: id } : {});
  const [showCreate, setShowCreate] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showSandbox, setShowSandbox] = useState(false);
  const [savingBlend, setSavingBlend] = useState(null);

  const qs = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
  const { data, loading, refetch } = useApi(`/recipe-dev${qs}`, [statusFilter]);
  const projects = data?.projects ?? data ?? [];

  if (showLibrary) return <DescriptorLibrary onBack={() => setShowLibrary(false)} />;

  // Sandbox: try out a syrup + spirit blend before it's a recipe (Joe, 27 Oct 2026).
  if (showSandbox) return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Btn variant="ghost" size="sm" onClick={() => setShowSandbox(false)}>← Projects</Btn>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Sandbox</h1>
      </div>
      {savingBlend && <SaveBlendModal blend={savingBlend} projects={projects} onClose={() => setSavingBlend(null)}
        onSaved={(pid, vid) => { setSavingBlend(null); setShowSandbox(false); refetch(); setParams(vid ? { project: pid, version: vid } : { project: pid }); }} />}
      <SyrupCalculator onSave={setSavingBlend} />
    </div>
  );

  if (selectedId) return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 8px' }}>
      <ProjectDetail projectId={selectedId} onBack={() => setSelectedId(null)} onRefresh={refetch} />
    </div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      {showCreate && <CreateProjectModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}
      {showImport && <ImportFormModal onClose={() => setShowImport(false)} onDone={(pid) => { setShowImport(false); refetch(); if (pid) setSelectedId(pid); }} />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Recipe Development</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Contract client recipe projects — briefing, rounds, verdicts, and conversion to runs</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" onClick={() => setShowImport(true)}>Import development form</Btn>
          <Btn variant="ghost" onClick={() => setShowSandbox(true)}>Sandbox</Btn>
          <Btn variant="ghost" onClick={() => setShowLibrary(true)}>Descriptor library</Btn>
          <Btn onClick={() => setShowCreate(true)}>+ New Project</Btn>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        {['all', ...STATUSES].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: statusFilter === s ? 'var(--pld-green)' : 'var(--surface-2)', color: statusFilter === s ? '#fff' : 'var(--text-muted)' }}>{s === 'all' ? 'all' : (STATUS_LABEL[s] || s)}</button>
        ))}
      </div>

      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : projects.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
          <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>No projects found</p>
          <p style={{ fontSize: 13, margin: 0 }}>Start a new recipe development project above.</p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Ref', 'Project', 'Client', 'Spirit', 'Rounds', 'Development fee', 'Status', 'Updated'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projects.map(p => (
                <tr key={p.id} onClick={() => setSelectedId(p.id)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>{p.project_ref || p.id?.slice(0,8)}</td>
                  <td style={{ padding: '11px 14px', fontWeight: 600 }}>{p.project_name || p.spirit_type}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{p.client_name}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{p.spirit_type || '—'}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{p.round_count ?? 0} / 3</td>
                  <td style={{ padding: '11px 14px', fontWeight: 600 }}>{p.development_fee ? `£${Number(p.development_fee).toFixed(2)}` : '—'}</td>
                  <td style={{ padding: '11px 14px' }}><StatusBadge status={p.status} /></td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{fmt(p.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
